import { computed, watch } from "vue";
import { storeToRefs } from "pinia";
import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;
import {
  CreateCustomFieldAnswerDto,
  Currency,
  CustomerInput,
  DiscountType,
  LineType,
  OrderDiscountStatus,
  OrderLine,
  OrderSession,
  OrderToken,
  Payment,
  RefundMethodOption,
  TargetQuantity,
} from "@smeetz/order-common";
import {
  CancelOrderLinesDto,
  CancelOrderLinesResponse,
  CancelUnitItemsDto,
  CancelUnitItemsResponse,
  CancelLinesByTokenResponse,
  CreateOrderLineDto,
  CreateOrderLinesResponse,
  LineAddonType,
  Order,
  OrderJson,
  OrderLinePublicType,
  OrderLineSeatInput,
  OrderSessionState,
  OrderSummary,
  PaymentMethod,
  PaymentState,
  RemoveOrderLineResponse,
  SessionData,
  UncancelLinesByTokenResponse,
  UncancelUnitItemsResponse,
  UpdateOrderLineRefactoredDto,
  UpdateOrderLinesResponse,
  Warning,
  WarningType,
} from "@smeetz/orders";
import type {
  OrderOperationContext,
  PrePaymentHoldResult,
} from "@smeetz/orders";
import {
  PaymentStatus,
  TerminalPaymentServiceStatus,
} from "@smeetz/payment-common";
import { useOrderStore } from "@smeetz/order-store";
import { BookingStep } from "@/constants/booking";
import { PSBErrors } from "@/constants/errors/product-slot-booking";
import { ModalStatus } from "@/constants/modal";
import { OpenCashDrawerOptions } from "@/interfaces/cash-drawer";
import { LogLevel, LogService } from "@/services/logging";
import { newOperationContext } from "@/services/package-logger";
import {
  logOrderActionError,
  notifyOrderActionError,
  secretPrefix,
} from "@/utils/vue-helpers/order-action-errors.helper";
import {
  getAppliedCodePaymentType,
  getDiscountCodeErrorMessage,
  getPromocodeSuccessMessage,
} from "@/utils/vue-helpers/promocode";
import NotificationService from "@/services/notification.service";
import {
  getPaymentService,
  isPaymentInProgress,
} from "@/services/payment.service";
import { getOrderSessionData } from "@/services/order-session-data.service";
import FiskalyModule from "@/store/modules/fiskaly";
import ModalModule from "@/store/modules/modal";
import POSConfigModule from "@/store/modules/pos-config";
import PrinterConfigModule from "@/store/modules/printers-config";
import CustomersModule from "@/store/modules/customers";
import ReceiptModule from "@/store/modules/receipt";
import { useOrderMetaStore } from "@/stores/order-meta.store";
import {
  IProductCategory,
  IProductSlotPrice,
  PCTypes,
  SeatType,
} from "@/types/db/product-category";
import { saveOrderForSync } from "@/db/orders";
import { roundToNearestMultipleOf5 } from "@/utils/helpers/common";
import { processAbortEventV2 } from "@/utils/helpers/compliance-v2";
import { isTippingEnabled } from "@/utils/helpers/feature-flags";
import { reflectCompletedSessionInLocalStock } from "./order-stock.helper";
import { saveAnswerSyncForOrder } from "@/db/answer-sync";
import { showConfirm } from "@/utils/vue-helpers/confirm-dialog";
import { ITimeSlot } from "@/types/db/timeslots";
import { printCommonReceipt } from "../helpers/cross-platform";
import { mapPcTypeToOrderLineType } from "../helpers/orders";
import { isFrenchCompliant } from "./compliance";
import { activeSessionBalanceOf } from "../helpers/open-balance";
import { autoPrintTicket, printBookingReceipt, printLabels } from "./booking";
import {
  getOrderCustomFieldAnswers,
  mapToSyncCustomFieldAnswerDtos,
  extractCustomFieldFileRefs,
} from "./custom-fields.helper";

export const maxOrderTabsToShow = 4;

const maxBookingTabs = computed(() => {
  return POSConfigModule.maxBookingTabs;
});

/**
 * `true` when the selected order is being edited — i.e. it has at least one
 * previous (already-completed) session AND its current session is not yet
 * completed. Used to gate UI flows that differ between creating a fresh order
 * and amending an existing one.
 */
export const isOrderEdited = computed(() => {
  const orderStore = useOrderStore();
  const { selectedOrder } = storeToRefs(orderStore);

  return (
    selectedOrder.value?.orderSession?.state !== OrderSessionState.COMPLETED &&
    !!selectedOrder.value?.previousSessions?.length
  );
});

/**
 * A session is non-empty once it has been edited in any way — it carries
 * order lines, payments (whatever their state), discounts or vouchers.
 */
export function isNonEmptyOrderSession(session: OrderSession): boolean {
  return !!(
    session.orderLines?.length ||
    session.payments?.length ||
    session.discounts?.length ||
    session.vouchers?.length
  );
}

/**
 * `true` when any open order still carries content or money — a non-completed,
 * non-empty session. Used to warn the cashier before closing the till.
 */
export const hasNonEmptyOrders = computed(() => {
  const orderStore = useOrderStore();
  const { orders } = storeToRefs(orderStore);

  return Object.values(orders.value).some((order) => {
    const session = order.orderSession;
    if (!session || session.state === OrderSessionState.COMPLETED) {
      return false;
    }
    return isNonEmptyOrderSession(session);
  });
});

/**
 * Returns the newest non-empty order session of the order. Sessions that
 * were opened but never edited are skipped. `previousSessions` is ordered
 * oldest→newest (each completed session is appended by the orders package),
 * and the active session is always the newest, so no sorting is needed.
 */
export function getNewestNonEmptyOrderSession(
  order: OrderJson
): OrderSession | null {
  const sessions = [
    order.orderSession,
    ...[...(order.previousSessions ?? [])].reverse(),
  ];

  return sessions.find(isNonEmptyOrderSession) ?? null;
}

/**
 * Checks if a new order can be created based on tab limits and ongoing operations.
 * @returns `true` if a new order can be created, `false` otherwise.
 */
export function canCreateNewOrder(): boolean {
  if (isPaymentInProgress.value) {
    // don't open a new order while a terminal payment is in progress
    return false;
  }

  const orderStore = useOrderStore();
  if (orderStore.orderCount >= maxBookingTabs.value) {
    // no new orders allowed
    LogService.info(
      `Maximum number of orders reached: ${orderStore.orderCount}`
    );
    return false;
  }
  // if (BookingModule.promocodeBeingChecked) {
  //   return false;
  // }
  return true;
}

/**
 * Checks if the current order tab can be closed.
 * @returns `true` if the tab can be closed, `false` otherwise.
 */
export function canCloseOrderTab(): boolean {
  if (isPaymentInProgress.value) {
    // don't close the tab while a terminal payment is in progress
    return false;
  }
  // if (BookingModule.promocodeBeingChecked) {
  //   return false;
  // }
  return true;
}

/**
 * Checks if another order can be selected.
 * @returns `true` if another order can be selected, `false` otherwise.
 */
export function canSelectOtherOrder(): boolean {
  if (isPaymentInProgress.value) {
    // don't switch orders while a terminal payment is in progress
    return false;
  }
  // if (BookingModule.promocodeBeingChecked) {
  //   return false;
  // }
  return true;
}

/**
 * Selects an order by its reference and potentially swaps it into view if needed.
 * @param reference - The reference of the order to select.
 * @param options - Optional parameters.
 * @param options.fromMoreOrders - If true, swaps the order tab to make it visible.
 */
export function selectOrder(
  reference: string | null,
  options?: { fromMoreOrders: boolean }
): void {
  if (!canSelectOtherOrder()) {
    return;
  }
  if (!reference) {
    throw new Error(PSBErrors.HasNoUUID);
  }
  const orderStore = useOrderStore();
  if (!orderStore.orders[reference]) {
    LogService.info(`selectOrder: order ${reference} no longer exists`);
    return;
  }
  const ctx = newOperationContext();
  orderStore.selectOrder(reference, ctx);
  LogService.info(`Selected order tab ${reference}`, {
    requestId: ctx.requestId,
    fromMoreOrders: options?.fromMoreOrders ?? false,
  });
  if (isSelectedTabExpired(reference)) {
    // The session died while the tab was idle — handle it now that the
    // cashier lands on it. Checking on selection (instead of a background
    // timer) means a tab is never yanked away while another one is active.
    void handleExpiredOrderSession(reference);
    return;
  }
  POSConfigModule.selectGrid(POSConfigModule.firstGrid);
  if (options?.fromMoreOrders) {
    // swap two order tabs to make the newly selected one visible
    const orderKeys = Object.keys(orderStore.orders);
    const refToSwap = orderKeys[orderKeys.length - maxOrderTabsToShow];
    if (refToSwap) {
      swapOrders({
        ref1: reference,
        ref2: refToSwap,
      });
    }
  }
}

/**
 * Swaps the positions of two orders in the order list.
 * @param references - An object containing the references of the two orders to swap.
 */
export function swapOrders(references: { ref1: string; ref2: string }): void {
  const { ref1, ref2 } = references;

  const orderStore = useOrderStore();
  const { orders } = storeToRefs(orderStore);

  const orderEntries = Object.entries(orders.value);
  const index1 = orderEntries.findIndex(([key]) => key === ref1);
  const index2 = orderEntries.findIndex(([key]) => key === ref2);

  if (index1 !== -1 && index2 !== -1) {
    // Swap the entries
    [orderEntries[index1], orderEntries[index2]] = [
      orderEntries[index2],
      orderEntries[index1],
    ];
    orderStore.setOrders(Object.fromEntries(orderEntries));
  }
}

/**
 * Selects the last order in the list.
 */
/**
 * Selects the newest order that can still take sales, skipping tabs whose
 * session is completed or abandoned. Completed orders stay in the tabs map
 * until their end-of-sale cleanup runs, and blindly selecting one routes the
 * next scan or tap into a session the orders package refuses to modify
 * (SESSION_COMPLETED). Returns false when no sellable order exists — callers
 * create a fresh order in that case.
 */
export function selectLastSellableOrder(): boolean {
  const orderStore = useOrderStore();
  const sellable = Object.values(orderStore.orders).filter((order) => {
    const state = order.orderSession?.state;
    return (
      state !== OrderSessionState.COMPLETED &&
      state !== OrderSessionState.ABANDONED
    );
  });
  const target = sellable[sellable.length - 1];
  if (!target) {
    return false;
  }
  const ctx = newOperationContext();
  orderStore.selectOrder(target.reference, ctx);
  LogService.info(`Selected last sellable order ${target.reference}`, {
    requestId: ctx.requestId,
  });
  return true;
}

/**
 * Ensures the till has a selected order to sell on: creates the first order
 * when none exist, and re-selects the newest one when orders exist but none
 * is selected. The second case happens when a restore's saved selection no
 * longer resolves — the store then holds order tabs with no active order,
 * and every order action fails with "No selected order". Never throws:
 * creation failures are journaled and surfaced by createNewOrder, and the
 * cashier can still open an order from the navbar.
 */
export async function ensureSelectedOrder(): Promise<void> {
  const orderStore = useOrderStore();
  try {
    if (orderStore.orderCount === 0) {
      await createNewOrder();
    } else if (!orderStore.selectedOrder && !selectLastSellableOrder()) {
      // Tabs exist but every one of them is a finished sale — the till
      // still needs a sellable order.
      await createNewOrder();
    }
  } catch {
    // journaled + surfaced by the order helpers
  }
}

/**
 * Shows a warning toast for each non-blocking OPTIONAL_ADDON_SKIPPED warning
 * returned by an order-line mutation — an optional auto-addon ran out of
 * stock and was left out while the main line still changed. Mirrors the
 * cashier-facing behavior of the legacy client-side addon pre-check.
 */
function surfaceOrderLineWarnings(warnings?: Warning[]): void {
  for (const warning of warnings ?? []) {
    if (warning.type === WarningType.OPTIONAL_ADDON_SKIPPED) {
      NotificationService.showCustomMessage(
        t("availability.optional-addon-skipped", {
          addonName: warning.data?.addonName ?? "",
        }),
        "warning"
      );
    }
  }
}

/**
 * Creates a new order and its initial session.
 */
export async function createNewOrder(): Promise<void> {
  const orderStore = useOrderStore();
  const ctx = newOperationContext();
  let sessionData: SessionData | undefined;
  try {
    sessionData = getOrderSessionData();
    await orderStore.createOrder(sessionData, ctx);
    LogService.info(
      `Created new order ${orderStore.selectedOrder?.reference}`,
      {
        requestId: ctx.requestId,
        orderId: orderStore.selectedOrder?.id,
        orderSessionId: orderStore.selectedOrder?.orderSession?.id,
      }
    );
  } catch (error) {
    logOrderActionError(
      "creating new order",
      { requestId: ctx.requestId, sessionData },
      error
    );
    notifyOrderActionError(error);
    throw error;
  }
}

/**
 * Creates a new session for the currently selected order.
 */
export async function createNewOrderSession(): Promise<void> {
  const orderStore = useOrderStore();
  const orderReference = orderStore.selectedOrder?.reference;
  const ctx = newOperationContext();
  let sessionData: SessionData | undefined;
  try {
    sessionData = getOrderSessionData();
    await orderStore.createSession(sessionData, ctx);
    LogService.info(`Created new session on order ${orderReference}`, {
      requestId: ctx.requestId,
      orderId: orderStore.selectedOrder?.id,
      orderSessionId: orderStore.selectedOrder?.orderSession?.id,
    });
  } catch (error) {
    logOrderActionError(
      `creating new order session on order ${orderReference}`,
      {
        requestId: ctx.requestId,
        orderId: orderStore.selectedOrder?.id,
        sessionData,
      },
      error
    );
    notifyOrderActionError(error);
    throw error;
  }
}

/**
 * Confirms abandoning a non-completed order that would lose work.
 *
 * Returns `true` (no prompt) for a completed order or an empty one — nothing to
 * lose. For a non-completed order it warns that closing loses the content, and
 * for one carrying a completed (partial) payment it points the cashier to the
 * BackOffice reference, since that order is preserved rather than discarded.
 */
async function confirmCloseOrder(): Promise<boolean> {
  const orderStore = useOrderStore();
  const session = orderStore.selectedOrder?.orderSession;

  if (session?.state === OrderSessionState.COMPLETED) {
    return true;
  }

  const hasCompletedPayment = session?.payments?.some(
    (payment) => payment.state === PaymentState.COMPLETED
  );
  // Only the current session's content — the order summary aggregates previous
  // (completed) sessions too, so an untouched edit-reopen would falsely warn.
  const hasContent = session ? isNonEmptyOrderSession(session) : false;

  if (!hasCompletedPayment && !hasContent) {
    return true;
  }

  const confirmed = await showConfirm({
    title: t("common.warning"),
    message: hasCompletedPayment
      ? t("close-booking.confirm-closing-with-partial-payment")
      : `XXXX\n${t("close-booking.confirm-closing-tab-message")}`,
    highlight: hasCompletedPayment
      ? String(orderStore.selectedOrder?.reference ?? "")
      : orderStore.selectedOrder?.tabName,
    variant: "destructive",
    confirmText: t("button.confirm"),
    cancelText: t("button.back"),
  });

  // Journal the cashier's decision — closing a partially paid order is the
  // kind of destructive action investigations need to see explicitly.
  LogService.info(
    `Close order ${orderStore.selectedOrder?.reference}: cashier ${
      confirmed ? "confirmed" : "declined"
    } closing a ${hasCompletedPayment ? "partially paid" : "non-empty"} order`,
    {
      orderId: orderStore.selectedOrder?.id,
      balance: orderStore.selectedOrderSummary?.balance,
      paymentCount: session?.payments?.length ?? 0,
      lineCount: session?.orderLines?.length ?? 0,
    }
  );

  return confirmed;
}

/**
 * Removes every open order that has no content — a non-completed session
 * that was never edited (no lines, payments, discounts or vouchers). Empty
 * orders exist only locally (nothing was persisted for them), so they are
 * dropped from the store directly, together with their metadata. Used on
 * config reload so stale empty tabs don't accumulate before the fresh order
 * is created.
 */
export function removeEmptyOrders(): void {
  const orderStore = useOrderStore();
  const orderMetaStore = useOrderMetaStore();

  for (const order of Object.values(orderStore.orders)) {
    const session = order.orderSession;
    const isEmpty =
      session &&
      session.state !== OrderSessionState.COMPLETED &&
      !isNonEmptyOrderSession(session);
    if (isEmpty) {
      orderStore.removeOrder(order.reference);
      orderMetaStore.clearOrderMeta(order.id);
      LogService.info(`Removed empty order ${order.reference}`, {
        orderId: order.id,
      });
    }
  }
}

/**
 * Removes every unfinished order — a non-completed session that carries no
 * money: orders with a card payment still in flight on the terminal or with
 * a completed (partial) payment are kept, everything else is dropped from
 * the store together with its metadata. Runs when a config's timeslots are
 * (re)fetched so open tabs don't survive a config switch.
 */
export function clearUnfinishedOrders(): void {
  const orderStore = useOrderStore();
  const orderMetaStore = useOrderMetaStore();

  for (const order of Object.values(orderStore.orders)) {
    const session = order.orderSession;
    if (!session || session.state === OrderSessionState.COMPLETED) {
      continue;
    }

    // A card payment may still land on the terminal — keep the order.
    const hasCardPaymentInFlight = session.payments?.some(
      (payment) =>
        payment.paymentMethod === PaymentMethod.POS_ADYEN &&
        payment.state !== PaymentState.COMPLETED &&
        payment.state !== PaymentState.FAILED &&
        payment.state !== PaymentState.CANCELLED
    );

    // Money was already captured on this session — keep the order.
    const isPartiallyPaid = session.payments?.some(
      (payment) => payment.state === PaymentState.COMPLETED
    );

    if (hasCardPaymentInFlight || isPartiallyPaid) {
      continue;
    }

    orderStore.removeOrder(order.reference);
    orderMetaStore.clearOrderMeta(order.id);
    // Drop any fiscal artifacts recorded for this order (keyed by order id).
    FiskalyModule.removeSignatureQrCodes(order.id);
    FiskalyModule.removeTransaction(order.id);
    LogService.info(`Removed unfinished order ${order.reference}`, {
      orderId: order.id,
      lineCount: session.orderLines?.length ?? 0,
      paymentCount: session.payments?.length ?? 0,
    });
  }
}

/**
 * Removes an order and creates a new one if it was the last one.
 * @param order - The order object to remove.
 */
export async function removeOrder(order: OrderJson): Promise<void> {
  const orderStore = useOrderStore();

  if (!(await confirmCloseOrder())) {
    return;
  }

  try {
    if (
      orderStore.selectedOrder?.orderSession?.state !==
      OrderSessionState.COMPLETED
    ) {
      const hasCompletedPayment =
        orderStore.selectedOrder?.orderSession?.payments?.some(
          (payment) => payment.state === PaymentState.COMPLETED
        );
      if (hasCompletedPayment) {
        // The session carries money (e.g. a partial payment). The package refuses
        // to remove such a session, and discarding it would lose the payment
        // trail. Queue the order for sync and just close the tab instead.
        await saveOrderForSync(order);
      } else {
        // France (NF525): journal the abandon before dropping the session.
        // Fail-open inside — closing the tab is never blocked.
        await processAbortEventV2(order);
        await removeOrderSession();
      }
    }

    orderStore.removeOrder(order.reference);
    useOrderMetaStore().clearOrderMeta(order.id);
    LogService.info(`Removed order ${order.reference}`, {
      orderId: order.id,
      remainingOrders: orderStore.orderCount,
    });

    if (orderStore.orderCount === 0) {
      // Last order gone — restart the default tab numbering so the fresh
      // auto-created tab is "Order #1" again.
      useOrderMetaStore().resetTabCounter();
      await createNewOrder();
    } else if (!selectLastSellableOrder()) {
      // Only finished sales remain in the tabs — the flow that removed this
      // order (end of sale, tab close) continues on a fresh sellable one.
      await createNewOrder();
    }
  } catch (error) {
    logOrderActionError(
      `removing order ${order.reference}`,
      { orderId: order.id },
      error
    );
    throw error;
  }
}

/**
 * Installs the order-session expiry watcher. The order store flags
 * `expiredOrderReference` whenever one of its actions fails because the
 * session's TTL ran out (SESSION_EXPIRED); this watcher routes the flagged
 * order into `handleExpiredOrderSession`. Call once during app bootstrap.
 */
export function setupOrderSessionExpiryHandling(): void {
  const orderStore = useOrderStore();
  watch(
    () => orderStore.expiredOrderReference,
    (reference) => {
      if (reference) {
        void handleExpiredOrderSession(reference);
      }
    }
  );
}

/**
 * True when the given order's tab should be closed as expired on selection:
 * the session TTL has lapsed, the session isn't completed (a completed
 * session keeps its lapsed `expireAt`, which is meaningless), and it carries
 * no settled (authorized/completed) payment. Expired sessions with a settled
 * payment stay open on purpose — the orders package still allows completing
 * them, because a payment notification can land after the TTL lapses and
 * closing the tab would lose that payment.
 */
function isSelectedTabExpired(reference: string): boolean {
  const orderStore = useOrderStore();
  if (!orderStore.isOrderSessionExpiredFor(reference)) {
    return false;
  }
  const session = orderStore.orders[reference]?.orderSession;
  if (!session || session.state === OrderSessionState.COMPLETED) {
    return false;
  }
  return !hasSettledPayment(session);
}

/** True when the session carries a settled (authorized or completed) payment. */
function hasSettledPayment(session: OrderJson["orderSession"]): boolean {
  return !!session?.payments?.some(
    (payment) =>
      payment.state === PaymentState.AUTHORIZED ||
      payment.state === PaymentState.COMPLETED
  );
}

/** References currently being cleaned up, so concurrent detections (action
 * failure + tab selection) don't run the close/popup flow twice. */
const expiredOrdersBeingHandled = new Set<string>();

/**
 * Closes the tab of an order whose session has expired. The session is dead
 * server-side (every further update would fail with SESSION_EXPIRED), so the
 * order is dropped locally without abandoning the session on the backend.
 * Completed sessions and sessions with a settled payment are never closed
 * here — the latter stay open so the order can still be completed and the
 * payment captured. A session with failed payment attempts is handed to the
 * sync queue first so the trail is preserved. The cashier is informed with a
 * single-button popup.
 */
async function handleExpiredOrderSession(reference: string): Promise<void> {
  if (expiredOrdersBeingHandled.has(reference)) {
    return;
  }
  expiredOrdersBeingHandled.add(reference);

  const orderStore = useOrderStore();
  try {
    orderStore.acknowledgeExpiredOrder();
    const order = orderStore.orders[reference];
    if (!order) {
      return;
    }
    if (order.orderSession?.state === OrderSessionState.COMPLETED) {
      // A completed session was already synced at completion; its lapsed
      // expireAt carries no meaning, so there is nothing to clean up.
      return;
    }
    if (hasSettledPayment(order.orderSession)) {
      // Real money on the expired session — the tab must survive so the
      // order can still be completed and the payment captured (the orders
      // package allows completing an expired session that carries a settled
      // payment; other mutations keep failing with their own errors).
      LogService.info(
        `Order session expired but carries a settled payment, keeping the tab open, order id: ${order.id}, reference: ${reference}`,
        {
          orderId: order.id,
          reference,
          orderSessionId: order.orderSession?.id,
          expireAt: order.orderSession?.expireAt,
          paymentCount: order.orderSession?.payments?.length ?? 0,
        }
      );
      return;
    }

    // Logged BEFORE the removal: subsequent SESSION_EXPIRED error logs lose
    // their order meta (the order is gone from the store by the time their
    // catch runs), so this entry is the anchor to correlate them against.
    LogService.info(
      `Order session expired, closing its tab, order id: ${order.id}, reference: ${reference}`,
      {
        orderId: order.id,
        reference,
        orderSessionId: order.orderSession?.id,
        expireAt: order.orderSession?.expireAt,
        tabName: order.tabName,
        lineCount: order.orderSession?.orderLines?.length ?? 0,
        paymentCount: order.orderSession?.payments?.length ?? 0,
      }
    );

    const hasPayment = (order.orderSession?.payments?.length ?? 0) > 0;
    if (hasPayment) {
      // Only non-settled payment activity (failed/cancelled attempts) can
      // reach this point — keep its trail by handing the order to the sync
      // queue before dropping the tab.
      await saveOrderForSync(order);
    }

    orderStore.removeOrder(reference);
    useOrderMetaStore().clearOrderMeta(order.id);

    if (orderStore.orderCount === 0) {
      // Last order gone — restart the default tab numbering so the fresh
      // auto-created tab is "Order #1" again.
      useOrderMetaStore().resetTabCounter();
      await createNewOrder();
    } else if (!selectLastSellableOrder()) {
      // Only finished sales remain in the tabs — the till continues on a
      // fresh sellable order after the expired tab is dropped.
      await createNewOrder();
    }

    await showConfirm({
      title: t("common.warning"),
      message: t("tabs.session-expired-msg"),
      variant: "warning",
      cancelText: null,
    });
  } catch (error) {
    LogService.info(
      `Error handling expired order session ${reference}`,
      { error },
      LogLevel.Error
    );
  } finally {
    expiredOrdersBeingHandled.delete(reference);
  }
}

/**
 * Removes the current order session.
 * @returns A promise that resolves when the order session is removed.
 * @throws Will throw an error if there is no active order or if removing the order session fails.
 */
export async function removeOrderSession(): Promise<void> {
  const orderStore = useOrderStore();
  const orderReference = orderStore.selectedOrder?.reference;
  const orderSessionId = orderStore.selectedOrder?.orderSession?.id;
  const ctx = newOperationContext();

  try {
    await orderStore.removeOrderSession(ctx);
    LogService.info(`Removed order session on order ${orderReference}`, {
      requestId: ctx.requestId,
      orderSessionId,
    });
  } catch (error) {
    logOrderActionError(
      `removing order session on order ${orderReference}`,
      {
        requestId: ctx.requestId,
        orderId: orderStore.selectedOrder?.id,
        orderSessionId,
      },
      error
    );
    notifyOrderActionError(error);
    throw error;
  }
}

/**
 * Finds the order line to update for an upsert against the active order, or
 * `null` when no logical match exists anywhere on the order.
 *
 * Resolution order:
 * 1. A `PENDING`-state match — current-session line (fresh sale or addition
 *    line referencing a previous session). Its id and quantity drive the
 *    upsert.
 * 2. Otherwise, the original sale (no `mainLineId`) from a previous session.
 *    Return its id with `quantity = 0`; the package turns the subsequent
 *    update into an addition line on the active session.
 *
 * Only sale lines are considered (cancellation lines share the grouping key
 * but are not valid upsert targets).
 */
function findUpsertTarget(
  orderLine: CreateOrderLineDto,
  summaryLines: OrderSummary["orderLines"]
): { id: string; quantity: number; state: OrderSessionState } | null {
  const isAddon = orderLine.addonType !== undefined;
  const orderLineRetailOptionId =
    orderLine.itemType === OrderLinePublicType.RETAIL
      ? orderLine.retailOptionId
      : undefined;

  const matches = (l: OrderLine) => {
    if (l.lineType !== LineType.SaleLine) return false;
    if (l.priceId !== orderLine.priceId) return false;
    if ((l.timeSlotId ?? null) !== (orderLine.timeSlotId ?? null)) return false;
    if (l.itemType !== orderLine.itemType) return false;
    if (
      orderLine.itemType === OrderLinePublicType.RETAIL &&
      (l.retailOptionId ?? null) !== (orderLineRetailOptionId ?? null)
    ) {
      return false;
    }
    if (isAddon) {
      return (
        l.addonType === orderLine.addonType &&
        l.parentLineId === orderLine.parentLineId
      );
    }
    // Regular line: don't match addon lines that happen to share priceId/timeSlotId.
    return !l.addonType;
  };

  const matching = summaryLines.filter(matches);
  if (!matching.length) return null;

  const sessionMatch = matching.find(
    (l) => l.state === OrderSessionState.PENDING
  );
  if (sessionMatch) {
    return {
      id: sessionMatch.id,
      quantity: sessionMatch.quantity,
      state: sessionMatch.state,
    };
  }

  const mainLine = matching.find((l) => !l.mainLineId) ?? matching[0];
  return { id: mainLine.id, quantity: 0, state: mainLine.state };
}

/**
 * Creates or updates order lines depending on whether a matching line already
 * exists in the order recap. See {@link findUpsertTarget} for match rules.
 *
 * Use this when the caller does not know upfront whether the line exists.
 * Use createOrderLines / updateOrderLines directly when you know the intent.
 *
 * `seatType` is accepted as a sidecar field on the input (not part of
 * `CreateOrderLineDto`) so we can detect GA seated lines and override quantity
 * on PENDING-session updates instead of accumulating.
 *
 * @returns `{ createdLines, updatedLines }` — IDs split by which path each
 *   input took. Callers that need to branch on create vs update can use this;
 *   callers that just need any resulting ID can `[...createdLines, ...updatedLines]`.
 */
export async function upsertOrderLines(
  orderLines: (CreateOrderLineDto & { seatType?: string })[],
  options?: { validateMandatoryAddons?: boolean },
  // One operation context for the whole upsert: the create and update halves
  // below share it, so the entire user action traces under a single requestId.
  ctx: OrderOperationContext = newOperationContext()
): Promise<{ createdLines: string[]; updatedLines: string[] }> {
  const orderStore = useOrderStore();
  const { selectedOrderSummary: orderSummary } = storeToRefs(orderStore);

  const toCreate: CreateOrderLineDto[] = [];
  const toUpdate: UpdateOrderLineRefactoredDto[] = [];
  // Per input line: what findUpsertTarget resolved (the existing line an
  // update will target, or null → routed to create). This matching is the
  // logic unique to upsert, so it's what this function journals.
  const matches: {
    priceId: string;
    timeSlotId?: number | string;
    matched: {
      orderLineId: string;
      quantity: number;
      state: OrderSessionState;
    } | null;
  }[] = [];

  for (const orderLine of orderLines) {
    const existing = findUpsertTarget(orderLine, orderSummary.value.orderLines);
    matches.push({
      priceId: orderLine.priceId,
      timeSlotId: orderLine.timeSlotId,
      matched: existing
        ? {
            orderLineId: existing.id,
            quantity: existing.quantity,
            state: existing.state,
          }
        : null,
    });

    if (existing) {
      const isGA =
        orderLine.itemType === OrderLinePublicType.SEATED &&
        orderLine.seatType === "GeneralAdmissionArea";

      // For a GA line still in the current (PENDING) session, override the quantity.
      // For everything else (including completed GA lines), accumulate.
      const newQuantity =
        isGA && existing.state === OrderSessionState.PENDING
          ? orderLine.quantity
          : orderLine.quantity + existing.quantity;

      toUpdate.push({
        orderLineId: existing.id,
        quantity: newQuantity,
        seatsToAdd:
          "seats" in orderLine
            ? (
                orderLine as CreateOrderLineDto & {
                  seats?: OrderLineSeatInput[];
                }
              ).seats
            : undefined,
      });
    } else {
      toCreate.push(orderLine);
    }
  }

  // Journal the received payload and the match decisions; the routed
  // toCreate/toUpdate payloads and responses are journaled by
  // createOrderLines/updateOrderLines themselves.
  LogService.info(
    `Upserting order lines on order ${orderStore.selectedOrder?.reference}`,
    {
      requestId: ctx.requestId,
      payload: orderLines.map(compactOrderLineDto),
      matches,
    }
  );

  const createdLines: string[] = [];
  const updatedLines: string[] = [];

  if (toCreate.length) {
    const response = await createOrderLines(toCreate, options, ctx);
    createdLines.push(...response.createdOrderLines);
  }

  if (toUpdate.length) {
    const response = await updateOrderLines(toUpdate, options, ctx);
    updatedLines.push(...response.updatedOrderLines);
  }

  return { createdLines, updatedLines };
}

/**
 * Compact log form of a CreateOrderLineDto — the identifying fields, with
 * seats reduced to a count so seated payloads don't bloat the journal.
 */
function compactOrderLineDto(
  line: CreateOrderLineDto
): Record<string, unknown> {
  return {
    itemType: line.itemType,
    priceId: line.priceId,
    quantity: line.quantity,
    timeSlotId: line.timeSlotId,
    parentLineId: line.parentLineId,
    addonType: line.addonType,
    retailOptionId: (line as { retailOptionId?: string }).retailOptionId,
    overridePrice: line.overridePrice,
    seatCount: (line as { seats?: unknown[] }).seats?.length,
  };
}

/**
 * Creates new order lines on the current session.
 * Use when you know the lines do not already exist.
 * For upsert semantics (create or update based on existing lines), use {@link upsertOrderLines}.
 *
 * @param data - Order lines to create
 * @param options.validateMandatoryAddons - When true, validates mandatory selectable addons after creation
 * @returns Created order line IDs (including auto-generated addon lines)
 */
export async function createOrderLines(
  data: CreateOrderLineDto[],
  options?: { validateMandatoryAddons?: boolean },
  ctx: OrderOperationContext = newOperationContext()
): Promise<CreateOrderLinesResponse> {
  const orderStore = useOrderStore();
  const orderReference = orderStore.selectedOrder?.reference;
  try {
    const response = await orderStore.createOrderLines(data, options, ctx);
    LogService.info(`Created order lines on order ${orderReference}`, {
      requestId: ctx.requestId,
      orderSessionId: orderStore.selectedOrder?.orderSession?.id,
      payload: data.map(compactOrderLineDto),
      options,
      createdOrderLines: response.createdOrderLines,
    });
    return response;
  } catch (error) {
    logOrderActionError(
      `creating order lines on order ${orderReference}`,
      {
        requestId: ctx.requestId,
        orderId: orderStore.selectedOrder?.id,
        payload: data.map(compactOrderLineDto),
      },
      error
    );
    notifyOrderActionError(error);
    throw error;
  }
}

/**
 * Updates existing order lines on the current or previous sessions.
 * The `quantity` in each DTO is the desired quantity on the **current session only** (not cumulative with previous sessions).
 * Previous session line updates create new addition/reduction lines for audit trail.
 * For seated line quantity reduction, use {@link removeSeats} instead.
 *
 * @param data - Lines to update (`orderLineId`, `quantity`, optional `seatsToAdd`)
 * @param options.validateMandatoryAddons - When true, validates mandatory selectable addons after update
 * @returns Updated order line IDs
 */
export async function updateOrderLines(
  data: UpdateOrderLineRefactoredDto[],
  options?: { validateMandatoryAddons?: boolean },
  ctx: OrderOperationContext = newOperationContext()
): Promise<UpdateOrderLinesResponse> {
  const orderStore = useOrderStore();
  const orderReference = orderStore.selectedOrder?.reference;
  try {
    const response = await orderStore.updateOrderLines(data, options, ctx);
    LogService.info(`Updated order lines on order ${orderReference}`, {
      requestId: ctx.requestId,
      orderSessionId: orderStore.selectedOrder?.orderSession?.id,
      payload: data,
      options,
      updatedOrderLines: response.updatedOrderLines,
      warnings: response.warnings,
    });
    surfaceOrderLineWarnings(response.warnings);
    return response;
  } catch (error) {
    logOrderActionError(
      `updating order lines on order ${orderReference}`,
      {
        requestId: ctx.requestId,
        orderId: orderStore.selectedOrder?.id,
        payload: data,
      },
      error
    );
    notifyOrderActionError(error);
    throw error;
  }
}

/**
 * Cancels quantity on original sale lines from previous sessions.
 * Creates or updates cancellation lines on the current session for audit trail.
 * Supports GA and non-seated lines only. For individual seated tickets, use {@link cancelUnitItems}.
 * The `quantity` in each DTO is the **target total cancelled quantity**, not a delta.
 *
 * @param data - Lines to cancel with target total cancelled quantity per original line ID
 * @param options.validateMandatoryAddons - When true, validates mandatory selectable addons after changes
 * @returns Cancellation order line IDs created/updated on the current session
 */
export async function cancelOrderLines(
  data: CancelOrderLinesDto[],
  options?: { validateMandatoryAddons?: boolean },
  ctx: OrderOperationContext = newOperationContext()
): Promise<CancelOrderLinesResponse> {
  const orderStore = useOrderStore();
  const orderReference = orderStore.selectedOrder?.reference;
  try {
    const response = await orderStore.cancelOrderLines(data, options, ctx);
    LogService.info(`Cancelled order lines on order ${orderReference}`, {
      requestId: ctx.requestId,
      data,
      cancellationOrderLines: response.cancellationOrderLines,
    });
    return response;
  } catch (error) {
    logOrderActionError(
      `cancelling order lines on order ${orderReference}`,
      {
        requestId: ctx.requestId,
        orderId: orderStore.selectedOrder?.id,
        payload: data,
      },
      error
    );
    notifyOrderActionError(error);
    throw error;
  }
}

/**
 * Cancels specific unit items (individual seated tickets) from previous session lines.
 * Creates or updates CancelLine rows on the current session.
 * Also marks the corresponding tokens as CANCELLED.
 * For non-seated or GA lines, use {@link cancelOrderLines} instead.
 *
 * @param data - Unit items to cancel (orderLineId + unitItemId pairs)
 * @returns Cancelled unit item IDs
 */
export async function cancelUnitItems(
  data: CancelUnitItemsDto[],
  ctx: OrderOperationContext = newOperationContext()
): Promise<CancelUnitItemsResponse> {
  const orderStore = useOrderStore();
  const orderReference = orderStore.selectedOrder?.reference;
  try {
    const response = await orderStore.cancelUnitItems(data, ctx);
    LogService.info(`Cancelled unit items on order ${orderReference}`, {
      requestId: ctx.requestId,
      data,
      cancelledUnitItems: response.cancelledUnitItems,
    });
    return response;
  } catch (error) {
    logOrderActionError(
      `cancelling unit items on order ${orderReference}`,
      {
        requestId: ctx.requestId,
        orderId: orderStore.selectedOrder?.id,
        payload: data,
      },
      error
    );
    notifyOrderActionError(error);
    throw error;
  }
}

/**
 * Restores unit items previously cancelled on the current session.
 * Updates or removes the corresponding CancelLine rows.
 *
 * @param data - Unit items to restore (orderLineId + unitItemId pairs)
 * @returns Restored unit item IDs
 */
export async function uncancelUnitItems(
  data: CancelUnitItemsDto[],
  ctx: OrderOperationContext = newOperationContext()
): Promise<UncancelUnitItemsResponse> {
  const orderStore = useOrderStore();
  const orderReference = orderStore.selectedOrder?.reference;
  try {
    const response = await orderStore.uncancelUnitItems(data, ctx);
    LogService.info(`Uncancelled unit items on order ${orderReference}`, {
      requestId: ctx.requestId,
      data,
      uncancelledUnitItems: response.uncancelledUnitItems,
    });
    return response;
  } catch (error) {
    logOrderActionError(
      `uncancelling unit items on order ${orderReference}`,
      {
        requestId: ctx.requestId,
        orderId: orderStore.selectedOrder?.id,
        payload: data,
      },
      error
    );
    notifyOrderActionError(error);
    throw error;
  }
}

/**
 * Cancels order lines identified by token IDs, across current and previous sessions.
 * Marks the corresponding tokens as CANCELLED.
 * Use this when the caller has token IDs rather than order line IDs.
 *
 * @param tokenIds - Token IDs whose lines should be cancelled
 * @returns Cancelled token IDs
 */
export async function cancelLinesByToken(
  tokenIds: string[],
  ctx: OrderOperationContext = newOperationContext()
): Promise<CancelLinesByTokenResponse> {
  const orderStore = useOrderStore();
  const orderReference = orderStore.selectedOrder?.reference;
  try {
    const response = await orderStore.cancelLinesByToken(tokenIds, ctx);
    LogService.info(`Cancelled lines by token on order ${orderReference}`, {
      requestId: ctx.requestId,
      tokenIds,
      cancelledTokens: response.cancelledTokens,
    });
    return response;
  } catch (error) {
    logOrderActionError(
      `cancelling lines by token on order ${orderReference}`,
      {
        requestId: ctx.requestId,
        orderId: orderStore.selectedOrder?.id,
        tokenIds,
      },
      error
    );
    notifyOrderActionError(error);
    throw error;
  }
}

/**
 * Restores order lines previously cancelled by token on the current session.
 * Removes or reduces the corresponding cancellation lines.
 * Restores the token status from CANCELLED.
 *
 * @param tokenIds - Token IDs whose cancellations should be reversed
 * @returns Restored token IDs
 */
export async function uncancelLinesByToken(
  tokenIds: string[],
  ctx: OrderOperationContext = newOperationContext()
): Promise<UncancelLinesByTokenResponse> {
  const orderStore = useOrderStore();
  const orderReference = orderStore.selectedOrder?.reference;
  try {
    const response = await orderStore.uncancelLinesByToken(tokenIds, ctx);
    LogService.info(`Uncancelled lines by token on order ${orderReference}`, {
      requestId: ctx.requestId,
      tokenIds,
      uncancelledTokens: response.uncancelledTokens,
    });
    return response;
  } catch (error) {
    logOrderActionError(
      `uncancelling lines by token on order ${orderReference}`,
      {
        requestId: ctx.requestId,
        orderId: orderStore.selectedOrder?.id,
        tokenIds,
      },
      error
    );
    notifyOrderActionError(error);
    throw error;
  }
}

/**
 * Permanently removes order lines from the current session.
 * Only current session lines can be removed. For previous session lines, use {@link cancelOrderLines}.
 * Linked addon lines are removed automatically. Discount rows are stripped.
 * Seated lines release their seats.io holds.
 *
 * @param orderLineIds - IDs of lines to remove (must exist on the current session)
 * @returns All removed order line IDs (main lines + auto-removed addons)
 */
export async function removeOrderLines(
  orderLineIds: string[],
  ctx: OrderOperationContext = newOperationContext()
): Promise<RemoveOrderLineResponse> {
  const orderStore = useOrderStore();
  const orderReference = orderStore.selectedOrder?.reference;
  try {
    const response = await orderStore.removeOrderLines(orderLineIds, ctx);
    LogService.info(`Removed order lines on order ${orderReference}`, {
      requestId: ctx.requestId,
      orderLineIds,
      removedOrderLines: response.removedOrderLines,
    });
    return response;
  } catch (error) {
    logOrderActionError(
      `removing order lines on order ${orderReference}`,
      {
        requestId: ctx.requestId,
        orderId: orderStore.selectedOrder?.id,
        orderLineIds,
      },
      error
    );
    notifyOrderActionError(error);
    throw error;
  }
}

/**
 * Removes an order line from the current order.
 * @param orderLine - The order line to remove.
 * @returns A promise that resolves when the order line is removed.
 */
// export async function removeOrderLine(orderLine: OrderLine): Promise<void> {
//   const orderStore = useOrderStore();
//   try {
//     await orderStore.removeOrderLines([orderLine.id]);
//   } catch (error) {
//     LogService.info(`Error removing order line ${orderLine.id}`, { error });
//     throw error;
//   }
// }

/**
 * Removes seats from the active order line in the current session.
 * Two modes — pass exactly one:
 * - **Per-seat**: pass `seatIds` (the seat IDs to release).
 * - **GA**: pass `gaSeatId` plus `gaQuantity` (the new total GA quantity, not a delta).
 *
 * Seats are released from seats.io only if they were already booked; pending
 * holds are dropped without an external call. Use this instead of
 * {@link updateOrderLines} when reducing quantity on a seated line.
 *
 * @param seatIds - Seat IDs to remove (per-seat mode).
 * @param gaSeatId - GA seat ID whose total quantity is being reduced (GA mode).
 * @param gaQuantity - New target quantity for the GA seat (GA mode).
 */
export async function removeSeats(
  seatIds?: string[],
  gaSeatId?: string,
  gaQuantity?: number,
  ctx: OrderOperationContext = newOperationContext()
): Promise<void> {
  const orderStore = useOrderStore();
  const orderReference = orderStore.selectedOrder?.reference;
  try {
    await orderStore.removeSeats(seatIds, gaSeatId, gaQuantity, ctx);
    LogService.info(`Removed seats on order ${orderReference}`, {
      requestId: ctx.requestId,
      seatIds,
      gaSeatId,
      gaQuantity,
    });
  } catch (error) {
    logOrderActionError(
      `removing seats on order ${orderReference}`,
      {
        requestId: ctx.requestId,
        orderId: orderStore.selectedOrder?.id,
        seatIds,
        gaSeatId,
        gaQuantity,
      },
      error
    );
    notifyOrderActionError(error);
    throw error;
  }
}

/**
 * Applies a promo / gift-card / voucher / membership code to the active order.
 *
 * @param code - The code to apply.
 * @param targetOrderLineIds - Restrict application to these line IDs (omit to
 *   let the package apply across all eligible lines per the code's rules).
 * @param targetQuantities - Per-line target quantities for codes whose
 *   `useOnePer` rule needs explicit quantity targeting.
 */
export async function applyDiscountCode(
  code: string,
  targetOrderLineIds?: string[],
  targetQuantities?: TargetQuantity[],
  ctx: OrderOperationContext = newOperationContext()
): Promise<void> {
  const orderStore = useOrderStore();
  const orderReference = orderStore.selectedOrder?.reference;
  try {
    await orderStore.applyDiscountCode(
      code,
      targetOrderLineIds,
      targetQuantities,
      ctx
    );
    // A membership code sets the member as the session customer inside the
    // orders package; mirror it into the customers store so the UI shows it.
    await applyOrderCustomerToModule(orderStore.selectedOrder);
    // Prefix only — this single entry point also receives voucher and
    // gift-card codes, which are value-bearing credentials.
    LogService.info(`Applied discount code on order ${orderReference}`, {
      requestId: ctx.requestId,
      codePrefix: secretPrefix(code, 4),
      codeLength: code.length,
      targetOrderLineIds,
      targetQuantities,
    });
    const paymentType = getAppliedCodePaymentType(code);
    if (paymentType) {
      NotificationService.showCustomMessage(
        getPromocodeSuccessMessage(paymentType),
        "success"
      );
    }
  } catch (error) {
    logOrderActionError(
      `applying discount code on order ${orderReference}`,
      {
        requestId: ctx.requestId,
        orderId: orderStore.selectedOrder?.id,
        codePrefix: secretPrefix(code, 4),
        codeLength: code.length,
        targetOrderLineIds,
        targetQuantities,
      },
      error
    );
    notifyOrderActionError(error, getDiscountCodeErrorMessage(error));
    throw error;
  }
}

function discountCodeBlockMessage(
  result: PrePaymentHoldResult,
  displayCode: string
): string {
  switch (result.reason) {
    case "MAX_USAGE_EXCEEDED":
      return t("promocode.code-xxx-limit-reached").replace("XXX", displayCode);
    case "VOUCHER_OVERDRAWN":
      return t("promocode.code-xxx-overdrawn").replace("XXX", displayCode);
    case "BLACKLISTED":
    default:
      return t("promocode.code-xxx-cancelled").replace("XXX", displayCode);
  }
}

/**
 * Re-validates the session's applied discount codes against the backend right
 * before money moves (payment or zero-balance completion). A code's status can
 * change between apply-time and payment (blacklisted, usage limit reached on
 * another till, voucher balance consumed elsewhere).
 *
 * The order is persisted first — the backend loads it by id. Each blocked
 * code is removed from the session by the check itself (totals recalculated)
 * and surfaced with a notification, and the attempt is aborted so the cashier
 * continues on the recomputed balance. The check requires connectivity: when
 * it cannot run, the attempt is blocked rather than risking a sale on a dead
 * code — the backend runs the same check as an unbypassable guard at
 * completion.
 *
 * No-op (allowed) when the session carries no discount codes.
 *
 * @returns true when the payment/completion can proceed
 */
export async function ensureDiscountCodesUsable(
  ctx: OrderOperationContext = newOperationContext()
): Promise<boolean> {
  const orderStore = useOrderStore();
  const order = orderStore.selectedOrder;
  // Same scoping as the backend check: only code-bearing, non-abandoned
  // discount configs are re-validated.
  const codeDiscounts = (order?.orderSession?.orderDiscounts ?? []).filter(
    (discount) =>
      discount.codeId != null &&
      discount.status !== OrderDiscountStatus.Abandoned
  );
  if (!order || !codeDiscounts.length) {
    return true;
  }

  ModalModule.setStatus(ModalStatus.PromocodeProcessing);
  try {
    // The backend loads the applied codes from the persisted order.
    await orderStore.persistOrder(ctx);

    const { removed } = await orderStore.checkPrePaymentHolds(ctx);
    if (!removed.length) {
      return true;
    }

    LogService.info(
      `Discount codes removed by the pre-payment check on order ${order.reference}`,
      {
        requestId: ctx.requestId,
        orderId: order.id,
        removed: removed.map(({ codeId, reason }) => ({ codeId, reason })),
      }
    );

    for (const blocked of removed) {
      NotificationService.showCustomError(
        discountCodeBlockMessage(
          blocked,
          blocked.code ?? String(blocked.codeId)
        )
      );
    }

    // The check recalculated the session without the removed codes — keep the
    // queued snapshot aligned.
    await saveOrderForSync(orderStore.selectedOrder);
    return false;
  } catch (error) {
    logOrderActionError(
      `re-validating discount codes on order ${order.reference}`,
      { requestId: ctx.requestId, orderId: order.id },
      error
    );
    NotificationService.showCustomError(t("promocode.couldnt-process"));
    return false;
  } finally {
    ModalModule.setStatus(ModalStatus.Hidden);
  }
}

/**
 * Applies a manual (cashier override) FLAT or PERCENT discount.
 *
 * @param amount - For FLAT: currency units (e.g. 10 = 10 CHF).
 *   For PERCENT: whole number 0-100 (e.g. 10 = 10%).
 * @param type - DiscountType.FLAT or DiscountType.PERCENT.
 * @param targetOrderLineIds - Restrict application to these line IDs (omit to
 *   apply across all eligible lines).
 */
export async function applyManualDiscount(
  amount: number,
  type: DiscountType.FLAT | DiscountType.PERCENT,
  targetOrderLineIds?: string[],
  ctx: OrderOperationContext = newOperationContext()
): Promise<void> {
  const orderStore = useOrderStore();
  const orderReference = orderStore.selectedOrder?.reference;
  try {
    await orderStore.applyManualDiscount(amount, type, targetOrderLineIds, ctx);
    LogService.info(`Applied manual discount on order ${orderReference}`, {
      requestId: ctx.requestId,
      amount,
      type,
      targetOrderLineIds,
    });
  } catch (error) {
    logOrderActionError(
      `applying manual discount on order ${orderReference}`,
      {
        requestId: ctx.requestId,
        orderId: orderStore.selectedOrder?.id,
        amount,
        type,
        targetOrderLineIds,
      },
      error
    );
    notifyOrderActionError(error);
    throw error;
  }
}

/**
 * Whether the selected order has an applied voucher or gift-card discount.
 * Vouchers and gift cards both use `DiscountType.VOUCHER` (regular promo codes use
 * `PROMOCODE`); one applied can bring the balance to zero without a cash/card payment.
 */
export function isVoucherOrGiftCardApplied(): boolean {
  const orderStore = useOrderStore();
  return (orderStore.selectedOrderSummary?.orderDiscounts ?? []).some(
    (discount) =>
      discount.type === DiscountType.VOUCHER &&
      discount.status === OrderDiscountStatus.Applied
  );
}

/**
 * Removes a previously-applied discount (manual, promo code, voucher, or offer)
 * from the active order session.
 *
 * For PENDING sessions the OrderDiscount and its line allocations are deleted
 * outright. For COMPLETED sessions, negating entries are created in the active
 * session for audit trail (the original records are preserved).
 *
 * @param orderDiscountId - The `OrderDiscount.id` to remove (not a line-level
 *   `Discount.id`). Read from `orderSession.orderDiscounts[].id`.
 */
export async function removeDiscount(
  orderDiscountId: string,
  ctx: OrderOperationContext = newOperationContext()
): Promise<void> {
  const orderStore = useOrderStore();
  const orderReference = orderStore.selectedOrder?.reference;
  try {
    await orderStore.removeDiscount(orderDiscountId, ctx);
    LogService.info(`Removed discount on order ${orderReference}`, {
      requestId: ctx.requestId,
      orderDiscountId,
    });
  } catch (error) {
    logOrderActionError(
      `removing discount on order ${orderReference}`,
      {
        requestId: ctx.requestId,
        orderId: orderStore.selectedOrder?.id,
        orderDiscountId,
      },
      error
    );
    notifyOrderActionError(error);
    throw error;
  }
}

/**
 * Creates a new seats io hold token for the current order.
 * @returns A promise that resolves with the new seats io hold token.
 */
export async function createSeatsIoHoldToken(): Promise<string> {
  const orderStore = useOrderStore();
  const orderReference = orderStore.selectedOrder?.reference;
  const ctx = newOperationContext();
  try {
    const { holdToken } = await orderStore.createSeatsIoHoldToken(ctx);
    LogService.info(
      `Created seats.io hold token for order ${orderReference}`,
      // Prefix only — the full hold token controls the seat holds.
      { requestId: ctx.requestId, holdTokenPrefix: secretPrefix(holdToken) }
    );
    return holdToken;
  } catch (error) {
    logOrderActionError(
      `creating seats io hold token on order ${orderReference}`,
      { requestId: ctx.requestId, orderId: orderStore.selectedOrder?.id },
      error
    );
    throw error;
  }
}

/**
 * Seat IDs already selected on the order for the given seats.io event keys, used
 * to pre-select them on the customer-display chart. Reads sale lines across all
 * sessions; a released seat is removed from the order, so only currently-held
 * seats remain.
 */
export function getSelectedSeatIdsForEvents(eventKeys: string[]): string[] {
  const orderStore = useOrderStore();
  return (orderStore.selectedOrderSummary?.orderLines ?? [])
    .filter(
      (line) =>
        line.lineType === LineType.SaleLine &&
        line.eventKey &&
        eventKeys.includes(line.eventKey)
    )
    .flatMap((line) => line.seats ?? [])
    .map((seat) => seat.seatId)
    .filter((seatId): seatId is string => Boolean(seatId));
}

/**
 * Scan (check in) a ticket by its token. Local-only and offline-safe: marks the
 * matching order token as scanned (`scannedAt`/`scannedBy`) in the order state
 * without any network call. Backend propagation is deferred to `syncScannedTokens`.
 */
export async function scanByToken(
  token: string,
  scannedBy?: number,
  ctx: OrderOperationContext = newOperationContext()
): Promise<void> {
  const orderStore = useOrderStore();
  const orderReference = orderStore.selectedOrder?.reference;
  try {
    await orderStore.scanByToken(token, scannedBy, ctx);
    // Only a token prefix — the full value is a scannable credential.
    LogService.info(`Scanned token on order ${orderReference}`, {
      requestId: ctx.requestId,
      tokenPrefix: secretPrefix(token),
      scannedBy,
    });
  } catch (error) {
    logOrderActionError(
      "scanning token",
      { requestId: ctx.requestId, tokenPrefix: secretPrefix(token), scannedBy },
      error
    );
    throw error;
  }
}

/**
 * Builds a CreateOrderLineDto object for a main product or an addon.
 *
 * Pass `parentLineId` to build an addon line attached to an existing main
 * line; `addonType` defaults to `LineAddonType.REGULAR` in that case.
 * Passing `addonType` without `parentLineId` is rejected to surface misuse.
 *
 * `seatType` is carried as a sidecar field on the result for SEATED lines so
 * downstream upsert logic can distinguish GA from per-seat lines.
 *
 * `overridePrice` (major units, e.g. 8.99) is applied as the per-unit price on
 * the created line, bypassing the catalog price. Caller is responsible for
 * ensuring the price is editable.
 *
 * @param data - The data required to build the order line.
 * @returns A CreateOrderLineDto object ready to be sent to the order store.
 * @throws If `addonType` is set without `parentLineId`, or if the resolved
 *   `itemType` is not currently supported by the POS flow.
 */
export function buildOrderLineDto(data: {
  productCategory: IProductCategory;
  price: IProductSlotPrice;
  quantity: number;
  timeslot?: ITimeSlot | null;
  eventKey?: string;
  seats?: OrderLineSeatInput[];
  seatType?: SeatType;
  parentLineId?: string;
  addonType?: LineAddonType;
  overridePrice?: number;
  parentSeatingType?: PCTypes;
}): CreateOrderLineDto {
  const {
    productCategory,
    price,
    quantity,
    timeslot,
    eventKey,
    seats,
    seatType,
    parentLineId,
    addonType,
    overridePrice,
    parentSeatingType,
  } = data;

  if (addonType !== undefined && parentLineId === undefined) {
    throw new Error(
      "buildOrderLineDto: `addonType` requires `parentLineId` (an addon line must reference its parent)."
    );
  }

  let itemType = mapPcTypeToOrderLineType(
    productCategory.type as unknown as PCTypes
  );

  // ORD-555 (membership seating plan): in a seating plan, `productCategory` is
  // the child seatedSub PC (type Seated), but the seating-plan parent may be a
  // MembershipSeated (type 17) product. `parentSeatingType` carries the parent
  // PC type so the line is sent as MEMBERSHIP_SEATED rather than SEATED, which
  // the backend needs to sync membership-seating tickets as pss type 17.
  // NOTE: wired here to preserve the prod ORD-555 behavior through the new
  // order-line flow; once seating is owned by @smeetz/orders this mapping
  // should move into the orders package.
  if (
    parentSeatingType !== undefined &&
    (itemType === OrderLinePublicType.SEATED ||
      itemType === OrderLinePublicType.MEMBERSHIP_SEATED)
  ) {
    itemType = mapPcTypeToOrderLineType(parentSeatingType);
  }

  const baseOrderLine: Omit<CreateOrderLineDto, "itemType"> = {
    priceId: String(price.uuid),
    quantity: quantity,
    timeSlotId: timeslot?.id,
  };

  if (parentLineId !== undefined) {
    baseOrderLine.parentLineId = parentLineId;
    baseOrderLine.addonType = addonType ?? LineAddonType.REGULAR;
  }

  if (overridePrice !== undefined) {
    baseOrderLine.overridePrice = overridePrice;
  }

  switch (itemType) {
    case OrderLinePublicType.TICKET:
    case OrderLinePublicType.VOUCHER:
    case OrderLinePublicType.MEMBERSHIP:
    case OrderLinePublicType.MEMBERSHIP_VOUCHER:
      return { ...baseOrderLine, itemType };
    case OrderLinePublicType.SEATED:
    case OrderLinePublicType.MEMBERSHIP_SEATED:
      return {
        ...baseOrderLine,
        itemType,
        eventKey,
        seats,
        seatType,
      } as CreateOrderLineDto & { seatType?: SeatType };
    case OrderLinePublicType.RETAIL:
      return {
        ...baseOrderLine,
        itemType,
        retailOptionId: String(price.retail[0].uuid),
      };
    default:
      throw new Error(
        `buildOrderLineDto: unsupported itemType "${itemType}" for product category ${productCategory.id}.`
      );
  }
}

/**
 * Log-safe view of a fetched order: the full operational state (sessions,
 * lines, payments, discounts) with the customer identity on each session
 * reduced to its id, so name/email never reach the log backend.
 */
function redactOrderForLog(order: OrderJson): OrderJson {
  const redactSession = <T extends { customer?: { id: string } }>(
    session: T
  ): T =>
    session?.customer
      ? { ...session, customer: { id: session.customer.id } }
      : session;

  return {
    ...order,
    orderSession: order.orderSession
      ? redactSession(order.orderSession)
      : order.orderSession,
    previousSessions: order.previousSessions?.map(redactSession),
  };
}

/**
 * Fetches an existing order from the backend by its public reference and loads it
 * into the order store as the active order.
 *
 * @param reference - The public order reference (e.g. "AB12CD34EF")
 */
export async function fetchOrderByReference(
  reference: string
): Promise<OrderJson> {
  const orderStore = useOrderStore();
  const ctx = newOperationContext();
  try {
    const order = await orderStore.fetchOrderByReference(reference, ctx);
    // Full order snapshot: pulled orders drive edit flows, so the exact state
    // received from the backend is what later mutations are judged against.
    LogService.info(`Fetched order by reference ${reference}`, {
      requestId: ctx.requestId,
      orderId: order.id,
      order: redactOrderForLog(order),
    });
    return order;
  } catch (error) {
    logOrderActionError(
      "pulling order",
      { requestId: ctx.requestId, reference },
      error
    );
    throw error;
  }
}

/**
 * Fetch (and load into the store) an order by a scanned ticket token — for QR
 * order-retrieval. Resolves the token to its order via the order-services
 * token→order lookup (`GET /v1/orders/public/token/{token}`) and returns it so
 * callers can open it via `openOrder`.
 */
export async function fetchOrderByToken(token: string): Promise<OrderJson> {
  const orderStore = useOrderStore();
  const ctx = newOperationContext();
  try {
    const order = await orderStore.fetchOrderByToken(token, ctx);
    // Tokens are credentials (they grant order retrieval); journal only a
    // prefix and correlate through the order reference/id.
    // Full order snapshot — see fetchOrderByReference.
    LogService.info(`Fetched order by token`, {
      requestId: ctx.requestId,
      tokenPrefix: secretPrefix(token),
      orderId: order.id,
      reference: order.reference,
      order: redactOrderForLog(order),
    });
    return order;
  } catch (error) {
    logOrderActionError(
      "pulling order by token",
      { requestId: ctx.requestId, tokenPrefix: secretPrefix(token) },
      error
    );
    throw error;
  }
}

/**
 * Fetches tokens for the selected order from the backend.
 * Should be called after a successful fetchOrderByReference
 * so the tokens are set on the active order object.
 */
export async function loadTokens(): Promise<OrderToken[]> {
  const orderStore = useOrderStore();
  const ctx = newOperationContext();
  const tokens = await orderStore.loadTokens(ctx);
  LogService.info(
    `Loaded tokens for order ${orderStore.selectedOrder?.reference}`,
    { requestId: ctx.requestId, tokenCount: tokens.length }
  );
  return tokens;
}

/**
 * Reads an order with its tokens for the check-in page, without touching the
 * order store: the store copy when this POS holds the order with its tokens
 * (an order just sold may not have reached the backend yet), otherwise a
 * backend read that is never stored, so no tab is added and the basket keeps
 * its active order.
 *
 * @param reference - The public order reference
 * @returns The order, and whether it is the copy this POS holds
 */
export async function readOrderForCheckIn(
  reference: string
): Promise<{ order: OrderJson; isLocal: boolean }> {
  const stored = useOrderStore().orders[reference];

  if (stored?.tokens?.length) {
    return { order: stored, isLocal: true };
  }

  const ctx = newOperationContext();
  try {
    const instance = await Order.fetchOrderDataByReference(reference, ctx);
    const tokens = await instance.loadTokens();
    LogService.info(`Read order ${reference} for check-in`, {
      requestId: ctx.requestId,
      orderId: instance.id,
      tokenCount: tokens.length,
    });
    return { order: instance.toJson(), isLocal: false };
  } catch (error) {
    logOrderActionError(
      "reading order for check-in",
      { requestId: ctx.requestId, reference },
      error
    );
    throw error;
  }
}

/**
 * Resolves a scanned ticket token to the reference of its order, without
 * loading the order into the store.
 *
 * @param token - The scanned ticket token
 * @returns The order reference
 */
export async function fetchOrderReferenceByToken(
  token: string
): Promise<string> {
  const ctx = newOperationContext();
  try {
    const instance = await Order.fetchOrderByToken(token, ctx);
    // Tokens are credentials (they grant order retrieval); journal only a prefix.
    LogService.info(`Resolved order reference by token`, {
      requestId: ctx.requestId,
      tokenPrefix: secretPrefix(token),
      orderId: instance.id,
      reference: instance.reference,
    });
    return instance.reference;
  } catch (error) {
    logOrderActionError(
      "pulling order by token for check-in",
      { requestId: ctx.requestId, tokenPrefix: secretPrefix(token) },
      error
    );
    throw error;
  }
}

/**
 * Reloads the tokens of the selected order from the backend when it is the
 * given order, so the basket shows the scan state a check-in just changed.
 * Any other order is left alone. A failure is logged and never thrown.
 *
 * @param reference - The order a check-in acted on
 */
export async function reloadSelectedOrderTokens(
  reference: string
): Promise<void> {
  if (useOrderStore().selectedOrder?.reference !== reference) {
    return;
  }

  try {
    await loadTokens();
  } catch (error) {
    LogService.info(
      `Could not reload the tokens of order ${reference} after a check-in`,
      { params: { error } },
      LogLevel.Error
    );
  }
}

/**
 * Reads the currently-selected customer (id, email, firstName, lastName) from
 * `CustomersModule` (keyed by the active order id), builds a `CustomerInput`,
 * and applies it to the active order via `orderStore.setCustomer`.
 *
 * No-ops when no customer is assigned to the active order.
 */
export function setCustomer(
  ctx: OrderOperationContext = newOperationContext()
): void {
  const email = CustomersModule.getSelectedCustomerEmail;
  // No customer assigned to this order: bail out before building the payload so we
  // don't hit the order session's EMAIL_REQUIRED guard (e.g. free / zero-balance sales).
  if (!email) {
    return;
  }

  const uuid = CustomersModule.getSelectedCustomerUuid;
  const firstName = CustomersModule.getSelectedCustomerFirstName;
  const lastName = CustomersModule.getSelectedCustomerLastName;

  const customer: CustomerInput = {
    id: uuid || undefined,
    email,
    lastName,
    firstName,
  };

  const orderStore = useOrderStore();
  orderStore.setCustomer(customer, ctx);
  LogService.info(
    `Set customer on order ${orderStore.selectedOrder?.reference}`,
    { requestId: ctx.requestId, customerId: uuid || undefined }
  );
}

/**
 * Mirror of `setCustomer`: reflects the order session's customer in
 * `CustomersModule` (keyed by `orderId`) so it shows in the UI. Runs after an
 * order is loaded and after a membership code sets the member as customer.
 * Only the fields carried by the session customer (id, name, email, zipCode,
 * marketing opt-in) are available.
 */
export async function applyOrderCustomerToModule(
  order: ReturnType<typeof useOrderStore>["selectedOrder"]
): Promise<void> {
  const customer = order?.orderSession?.customer;
  if (!customer) {
    return;
  }
  const { id, firstName, lastName, email, zipCode, emailMarketingOrganiser } =
    customer;

  await CustomersModule.setSelectedCustomers([
    {
      id,
      orderId: order?.id,
      checkOrder: true,
      fullName: [firstName, lastName].filter(Boolean).join(" "),
      firstName,
      lastName,
      email,
      zipCode,
      emailMarketingOrganiser,
    },
  ]);
}

/**
 * Adds a custom field answer to the active order session.
 * The answer value itself is never journaled — custom field answers carry
 * visitor-provided data (names, emails, documents).
 *
 * @param answer - The custom field answer data.
 * @param ctx - Per-operation context; a confirm press saving several answers
 *   passes one shared ctx so the whole action traces under a single requestId.
 */
export function createCustomFieldAnswer(
  answer: CreateCustomFieldAnswerDto,
  ctx: OrderOperationContext = newOperationContext()
): void {
  const orderStore = useOrderStore();
  const orderReference = orderStore.selectedOrder?.reference;
  try {
    orderStore.createCustomFieldAnswer(answer, ctx);
    LogService.info(`Created custom field answer on order ${orderReference}`, {
      requestId: ctx.requestId,
      fieldId: answer.fieldId,
      orderLineId: answer.orderLineId,
      unitItemId: answer.unitItemId,
    });
  } catch (error) {
    logOrderActionError(
      `creating custom field answer on order ${orderReference}`,
      {
        requestId: ctx.requestId,
        orderId: orderStore.selectedOrder?.id,
        fieldId: answer.fieldId,
      },
      error
    );
    notifyOrderActionError(error);
    throw error;
  }
}

/**
 * Clears every order from the store. Used when closing the till so the next
 * open starts with fresh orders and tab numbering.
 */
export function clearOrders(): void {
  const orderStore = useOrderStore();
  const orderCount = orderStore.orderCount;
  orderStore.setOrders({});
  LogService.info("Cleared all orders from the store", { orderCount });
}

/**
 * Increment the receipt counter on completion so the receipt carries a sequential
 * number. No-op when the POS has no receipt counter configured. Must run before the
 * order is queued for sync. Shared by the zero-balance and paid completion paths.
 */
async function applyReceiptCounterToSession(): Promise<void> {
  if (!ReceiptModule.receiptCounter) {
    return;
  }
  try {
    await ReceiptModule.manuallyIncrementAndPatch();

    // Stamp the number onto the session so it persists with the completion
    // snapshot — copy receipts read it back to recover the original number.
    // First number wins package-side, so a retried completion cannot renumber.
    if (ReceiptModule.receiptCounter) {
      useOrderStore().setReceiptCounter({
        counterId: ReceiptModule.receiptCounter.id,
        seqNumber: ReceiptModule.receiptCounter.value,
        receiptNumber: ReceiptModule.receiptCounter.formattedValue,
      });
    }
  } catch (error) {
    LogService.info(
      "Receipt counter: failed to increment or stamp the session",
      { error },
      LogLevel.Error
    );
  }
}

/**
 * Auto-print tickets/labels and the receipt for a just-completed order per the
 * printer/POS config. Shared by the zero-balance and paid completion paths.
 */
async function autoPrintCompletedOrder(): Promise<void> {
  if (PrinterConfigModule.hasAutoPrinting) {
    if (PrinterConfigModule.isTicketPrintingEnabled) {
      await autoPrintTicket();
    }
    if (PrinterConfigModule.isLabelPrintingEnabled) {
      await printLabels();
    }
  }

  if (PrinterConfigModule.autoPrintReceipt) {
    await printBookingReceipt();
  } else if (
    POSConfigModule.openCashDrawer === OpenCashDrawerOptions.AFTER_EVERY_SALE
  ) {
    printCommonReceipt("");
  }
}

/**
 * Finalizes the order: attach the customer, complete the session, stamp the
 * receipt counter, queue the order (then its custom-field answers) for sync and
 * auto-print. It takes no payment itself, so callers run it once nothing is
 * left to collect now: `processOrder` when the amount due is 0 (also right
 * after a payment that settles it) and `completeOrderWithBalance` once the
 * balance is deferred.
 */
async function finalizeOrder(
  ctx: OrderOperationContext,
  {
    customerAfterCompletion = false,
    beforeAutoPrint,
  }: {
    /**
     * The paid path attaches the customer only after `completeOrder` (its
     * historical order around the completion); still before queuing, so the
     * synced snapshot and the printed tickets/receipt include it.
     */
    customerAfterCompletion?: boolean;
    /** Runs after the order is queued, right before auto-print (cash drawer). */
    beforeAutoPrint?: () => void;
  } = {}
): Promise<void> {
  const orderMetaStore = useOrderMetaStore();
  const orderStore = useOrderStore();
  const { selectedOrder, customFieldAnswers } = storeToRefs(orderStore);

  if (!selectedOrder.value) {
    throw new Error(PSBErrors.NoBookingInModule);
  }

  if (!customerAfterCompletion) {
    setCustomer(ctx);
  }

  // completeOrder logs its own failures and throws on engine failure; the
  // France SALE fiscalizes off the order bus, fail-open (never blocks here).
  await completeOrder(ctx);
  orderMetaStore.setStep(BookingStep.CompleteOrder);

  if (customerAfterCompletion) {
    setCustomer(ctx);
  }

  // Stamp the receipt counter onto the session before queuing so it persists.
  await applyReceiptCounterToSession();

  // Write-ahead: queue the completed order to Dexie so it is durable even if
  // the network is down; the background drain persists it to the backend.
  await saveOrderForSync(selectedOrder.value);

  // Custom fields — enqueue AFTER the order so the answer-sync drain (gated on
  // the order being persisted first) never uploads answers before the order exists.
  if (customFieldAnswers.value.length > 0) {
    const answers = mapToSyncCustomFieldAnswerDtos(customFieldAnswers.value);
    const fileRefs = extractCustomFieldFileRefs(customFieldAnswers.value);
    await saveAnswerSyncForOrder(selectedOrder.value.id, answers, fileRefs);
  }

  beforeAutoPrint?.();

  await autoPrintCompletedOrder();
}

/**
 * Handles the entire order processing flow, from custom fields to payment and completion.
 * @returns A promise that resolves when processing is complete.
 */
export async function processOrder(
  // One operation context for the whole processing press: validation, payment
  // attachment and completion all trace under a single requestId. Callers
  // that already opened an operation (the custom-fields confirm flow) pass
  // theirs so the press stays one trace.
  ctx: OrderOperationContext = newOperationContext()
): Promise<void> {
  const orderMetaStore = useOrderMetaStore();
  const { step, paymentMethod, providedAmount, currency } =
    storeToRefs(orderMetaStore);

  const orderStore = useOrderStore();
  const {
    selectedOrder,
    selectedOrderSummary: orderSummary,
    customFieldAnswers,
  } = storeToRefs(orderStore);

  if (!selectedOrder.value) {
    throw new Error("No selected order available for processing.");
  }

  LogService.info(
    `Processing order ${selectedOrder.value.reference} at step ${step.value}`,
    {
      requestId: ctx.requestId,
      orderId: selectedOrder.value.id,
      orderSessionId: selectedOrder.value.orderSession?.id,
      step: step.value,
      balance: orderSummary.value.balance,
      paymentMethod: paymentMethod.value,
    }
  );

  // TODO: getOrderCustomFieldAnswers is called on every button press regardless of step,
  // causing unnecessary API calls when step is not BookingStep.Order.
  // Consider only calling this when step === BookingStep.Order.
  const { ticket: ticketCustomFields, booking: bookingCustomFields } =
    await getOrderCustomFieldAnswers(selectedOrder.value.id);
  const hasCustomFields =
    ticketCustomFields.length > 0 ||
    bookingCustomFields.length > 0 ||
    customFieldAnswers.value.length;

  if (
    hasCustomFields &&
    ModalModule.status === ModalStatus.Hidden &&
    step.value === BookingStep.Order
  ) {
    // So booking has customfields and customFields modal is not displayed (means the function wasn't called from customFields popup)
    // Lets display CustomFields popup
    LogService.info(
      `Order ${selectedOrder.value.reference} has custom fields, showing the custom fields popup`,
      {
        requestId: ctx.requestId,
        ticketCustomFields: ticketCustomFields.length,
        bookingCustomFields: bookingCustomFields.length,
        answers: customFieldAnswers.value.length,
      }
    );
    ModalModule.setStatus(ModalStatus.CustomFields);
    // Don't let the user go to paymentStep/completedStep if he has custom fields
    // He will do so once he finish custom fields flow
    return;
  } else if (ModalModule.status === ModalStatus.CustomFields) {
    ModalModule.setStatus(ModalStatus.Hidden);
  }

  // FIS-719: what is collected now is the session's own balance plus any debt
  // left open by previous visits; a cancellation credit is netted against
  // that debt, so a negative amount means the credit exceeds the debt.
  const totalBalance = computed(() => {
    return orderSummary.value.amountDue;
  });

  // No payment needed, only sync data if updated
  if (totalBalance.value === 0) {
    LogService.info(
      `Zero balance — completing order ${selectedOrder.value.reference} without payment`,
      { requestId: ctx.requestId }
    );

    // A zero balance can come from a fully-covering code — re-validate the
    // applied codes before the sale is finalized.
    if (!(await ensureDiscountCodesUsable(ctx))) {
      return;
    }

    await finalizeOrder(ctx);
    return;
  }

  // Payment
  if (totalBalance.value > 0) {
    if (step.value === BookingStep.Order) {
      try {
        // Pre-flight validation of the session's lines (availability,
        // mandatory addons) before entering the payment step.
        await orderStore.validateOrder(ctx);
      } catch (error) {
        logOrderActionError(
          `validating order ${selectedOrder.value.reference} before payment`,
          { requestId: ctx.requestId, balance: totalBalance.value },
          error
        );
        notifyOrderActionError(error);
        throw error;
      }

      LogService.info(
        `Order ${selectedOrder.value.reference} moved to the payment step`,
        { requestId: ctx.requestId, balance: totalBalance.value }
      );
      orderMetaStore.setStep(BookingStep.Payment);
      return;
    }

    if (step.value === BookingStep.Payment) {
      // An invoice-style payment (Swiss QR bank transfer) turns the order into an
      // invoice, which requires a customer. Block and prompt for one if none is
      // assigned.
      if (
        paymentMethod.value === PaymentMethod.Swiss_QR_Code_Bank_Transfer &&
        !CustomersModule.getSelectedCustomerId
      ) {
        LogService.info(
          `Order ${selectedOrder.value.reference}: Swiss QR payment requires a customer, showing the customers popup`,
          { requestId: ctx.requestId }
        );
        ModalModule.setStatus(ModalStatus.Customers);
        return;
      }

      // Applied discount codes must still be usable before any money moves.
      if (!(await ensureDiscountCodesUsable(ctx))) {
        return;
      }

      if (paymentMethod.value === PaymentMethod.POS_ADYEN) {
        LogService.info(
          `Order ${selectedOrder.value.reference}: terminal payment, persisting the order before contacting the terminal`,
          { requestId: ctx.requestId }
        );
        ModalModule.setStatus(ModalStatus.PaymentProgress);

        // Persist the order before contacting the terminal so a crash/hang can't
        // lose it (Adyen-only). Payment-less here; re-saved after setPayment.
        if (selectedOrder.value?.id) {
          await saveOrderForSync(selectedOrder.value);
        }
      }

      try {
        const paymentService = getPaymentService();

        const paymentCurrency = (currency.value?.label ||
          selectedOrder.value?.orderSession?.currency) as Currency;

        const cashPaymentDetails =
          paymentMethod.value !== PaymentMethod.CASH
            ? undefined
            : {
                exchangeRate: currency.value?.exchangeRate || 1,
                paymentCurrency,
                providedCashBill: Math.round(
                  (providedAmount.value || totalBalance.value) * 100
                ),
              };

        // Cash handed in another drawer currency counts in the order currency, as in the booking flow.
        const balanceCents = Math.round(totalBalance.value * 100);
        const exchangeRate = cashPaymentDetails?.exchangeRate || 1;
        let providedCents = Math.round(
          ((providedAmount.value || 0) / exchangeRate) * 100
        );
        // Only a partial payment in another currency of a CHF order settles to the 5 centimes; a full one pays the exact balance.
        if (
          cashPaymentDetails &&
          currency.value &&
          providedCents < balanceCents &&
          selectedOrder.value?.orderSession?.currency === Currency.CHF
        ) {
          providedCents = roundToNearestMultipleOf5(providedCents);
        }
        const paymentValueCents = Math.min(balanceCents, providedCents); // payments are handled in cents

        // FIS-715: the base the terminal calculates its suggested tip percentages
        // on, in cents. Only sent for terminal payments — its presence is what
        // turns the tip screen on.
        //
        // In Canada tips are suggested on the pre-tax amount, so the VAT this
        // payment carries is taken off. Payments are amount-based, not per item,
        // so that VAT is prorated by the share of the order total being paid; a
        // full payment subtracts the full VAT. Elsewhere the tip is suggested on
        // the full amount charged.
        const orderTotal = orderSummary.value.totalWithDiscount || 0;
        const vatInPaymentCents =
          POSConfigModule.getGroupCountryCode === "CA" && orderTotal > 0
            ? paymentValueCents *
              ((orderSummary.value.totalVat || 0) / orderTotal)
            : 0;
        // Tipping is rolled out per group through the pos::tipping flag: a group
        // without it pays by card without the terminal's tip screen.
        const amountToTipOn =
          paymentMethod.value === PaymentMethod.POS_ADYEN && isTippingEnabled()
            ? Math.max(0, Math.round(paymentValueCents - vatInPaymentCents))
            : undefined;

        LogService.info(
          `Initiating payment for order ${selectedOrder.value?.reference}`,
          {
            requestId: ctx.requestId,
            orderId: selectedOrder.value?.id,
            orderSessionId: selectedOrder.value?.orderSession?.id,
            paymentMethod: paymentMethod.value || PaymentMethod.CASH,
            valueCents: paymentValueCents,
            amountToTipOn,
            currency: paymentCurrency,
            providedAmount: providedAmount.value,
            balance: totalBalance.value,
          }
        );

        isPaymentInProgress.value = true;
        const response = await paymentService
          .createPayment({
            orderId: selectedOrder.value?.id as string,
            orderSessionId: selectedOrder.value?.orderSession?.id as string,
            value: paymentValueCents,
            currency: selectedOrder.value?.orderSession?.currency as Currency,
            paymentMethod: paymentMethod.value || PaymentMethod.CASH,
            cashPaymentDetails,
            amountToTipOn,
          })
          .finally(() => {
            isPaymentInProgress.value = false;
          });

        LogService.info(
          `Payment result for order ${selectedOrder.value?.reference}`,
          {
            requestId: ctx.requestId,
            orderId: selectedOrder.value?.id,
            status: response?.status,
            reason: response?.reason,
            paymentId: response?.payment?.id,
          }
        );

        if (!response || !response.payment) {
          throw new Error("Payment result is null");
        }

        await orderStore.setPayment(response.payment, ctx);
        LogService.info(
          `Payment attached to order ${selectedOrder.value?.reference}`,
          {
            requestId: ctx.requestId,
            paymentId: response.payment.id,
            state: response.payment.state,
          }
        );

        /*
         FIS-715: a gratuity taken on the terminal comes back as its own Payment.
         payment-common has already POSTed it to /v1/payments, so the money is
         banked either way — but without attaching it here the tip stays invisible
         in the app until the order is refetched: no summary row, nothing on the
         customer display, and nothing in the synced snapshot.

         setPayment is tip-aware: it records the row for traceability but skips the
         toPay reduction for a tip (isTip guard), so the order does not read as
         overpaid and the customer voucher is not released. It has to run here,
         before completeOrder below, because it requires a PENDING session.
        */
        if (response.tipPayment) {
          // FIS-830: the order keeps the tip on the session (OrderSession.tips) with the
          // base the terminal suggested its percentages on, frozen as it was sent.
          await orderStore.setPayment(response.tipPayment, ctx, {
            tipBaseAmount: amountToTipOn,
          });
          LogService.info(
            `Tip attached to order ${selectedOrder.value?.reference}`,
            {
              requestId: ctx.requestId,
              tipPaymentId: response.tipPayment.id,
              originalPaymentId: response.tipPayment.originalPaymentId,
              value: response.tipPayment.value,
              amountToTipOn,
            }
          );
        }

        ModalModule.setStatus(ModalStatus.Hidden);

        if (response.status === PaymentStatus.COMPLETED) {
          // If nothing is left to collect (session lines and open debt), we
          // can complete the order
          if (orderSummary.value.amountDue === 0) {
            await finalizeOrder(ctx, {
              customerAfterCompletion: true,
              beforeAutoPrint: () => {
                if (
                  POSConfigModule.openCashDrawer ===
                    OpenCashDrawerOptions.AFTER_CASH_PAYMENT &&
                  paymentMethod.value === PaymentMethod.CASH
                ) {
                  LogService.info("Opening cash drawer after cash payment");
                  printCommonReceipt("");
                }
              },
            });
          } else {
            // Partial payment: the order stays open on the payment step for
            // the remaining balance. Persist the payment we just took.
            LogService.info(
              `Partial payment on order ${selectedOrder.value?.reference}, remaining amount due ${orderSummary.value.amountDue}`,
              { requestId: ctx.requestId, orderId: selectedOrder.value?.id }
            );
            if (selectedOrder.value?.id) {
              await saveOrderForSync(selectedOrder.value);
            }
          }
          return;
        } else if (
          response.status === PaymentStatus.CANCELED ||
          response.status === PaymentStatus.FAILED
        ) {
          // Persist the failed/cancelled payment attempt so its trail survives.
          if (selectedOrder.value?.id) {
            await saveOrderForSync(selectedOrder.value);
          }

          // notify user
          const { title, message } = getPaymentStatusNotification(
            response.reason
          );
          NotificationService.showCustomMessageWithTitle(
            message,
            title,
            "warning"
          );
        }
      } catch (error) {
        // Swallowed on purpose (the cashier retries from the payment step), so
        // console.error keeps dev visibility while LogService ships it.
        console.error("Payment processing failed:", error);
        logOrderActionError(
          `processing payment for order ${selectedOrder.value?.reference}`,
          {
            requestId: ctx.requestId,
            orderId: selectedOrder.value?.id,
            orderSessionId: selectedOrder.value?.orderSession?.id,
            paymentMethod: paymentMethod.value,
          },
          error
        );
        ModalModule.setStatus(ModalStatus.Hidden);
      }
    }
  }

  // Refund — only when the credit exceeds the open debt (amountDue < 0)
  if (totalBalance.value < 0) {
    if (step.value === BookingStep.Order) {
      LogService.info(
        `Order ${selectedOrder.value.reference} has a negative balance, moving to the refund step`,
        { requestId: ctx.requestId, balance: totalBalance.value }
      );
      orderMetaStore.setStep(BookingStep.Refund);
      return;
    }

    if (step.value === BookingStep.Refund) {
      // A full refund zeroes the balance and completes via the zero-balance path;
      // reaching here just advances the UI to the completed step.
      LogService.info(
        `Order ${selectedOrder.value.reference} leaving the refund step to the completed step`,
        { requestId: ctx.requestId, balance: totalBalance.value }
      );
      orderMetaStore.setStep(BookingStep.CompleteOrder);
      return;
    }
  }
}

/**
 * Completes the selected order's session and reflects its net stock delta in
 * the local availability numbers. Always complete orders through this helper
 * (not the store action directly) so completion side effects stay in one
 * place; the stock reflection never throws — a completed sale must not fail
 * on a local bookkeeping step.
 *
 * France (NF525): the SALE is fiscalized off the order engine's event bus
 * (`ComplianceOrderSubscriber` on ORDER_SESSION_COMPLETED), not from here —
 * and the bus is fail-open by design (accepted tradeoff): a fiscal failure
 * is logged and surfaced to the operator but never blocks completion. The
 * operator session the bus SALE needs is restored proactively at
 * sessionReady (see ensureOperatorSessionV2).
 */
export async function completeOrder(
  ctx: OrderOperationContext = newOperationContext()
): Promise<void> {
  const orderStore = useOrderStore();
  const order = orderStore.selectedOrder;
  const summary = orderStore.selectedOrderSummary;

  LogService.info(`Completing order ${order?.reference}`, {
    requestId: ctx.requestId,
    orderId: order?.id,
    reference: order?.reference,
    orderSessionId: order?.orderSession?.id,
    lineCount: summary?.orderLines?.length,
    totalSales: summary?.totalSales,
    totalDiscounts: summary?.totalDiscounts,
    totalWithDiscount: summary?.totalWithDiscount,
    totalPayments: summary?.totalPayments,
    totalVouchers: summary?.totalVouchers,
    balance: summary?.balance,
  });

  try {
    await orderStore.completeOrder(ctx);
  } catch (error) {
    logOrderActionError(
      `completing order ${order?.reference}`,
      {
        requestId: ctx.requestId,
        orderId: order?.id,
        orderSessionId: order?.orderSession?.id,
      },
      error
    );
    throw error;
  }

  LogService.info(`Order ${order?.reference} completed`, {
    requestId: ctx.requestId,
    orderId: order?.id,
    orderSessionId: order?.orderSession?.id,
  });

  await reflectCompletedSessionInLocalStock(orderStore.selectedOrder);
}

/**
 * FIS-719 — "Complete with balance": the cashier closes the visit while money
 * is still owed. What this session still owes is deferred as one
 * ORGANIZER_EXPLICIT row, which brings the session balance to 0, then the order
 * completes through the no-payment path (customer, completion, receipt counter,
 * sync queue, custom-field answers, auto-print).
 *
 * The deferred row counts in the engine's `openBalance` as soon as it exists,
 * so `amountDue` does not drop to 0 here — completion is called directly
 * rather than through `processOrder`, which would route back to a payment.
 * When the session itself is already settled and only older debt is open,
 * there is nothing to defer: that debt stays open on its own row.
 */
export async function completeOrderWithBalance(
  ctx: OrderOperationContext = newOperationContext()
): Promise<void> {
  const orderStore = useOrderStore();
  const { selectedOrder } = storeToRefs(orderStore);

  const orderSession = selectedOrder.value?.orderSession;
  if (!orderSession) {
    throw new Error(PSBErrors.NoBookingInModule);
  }

  // Mirrors the button gate: NF525 forbids closing a sale while a balance
  // remains, so French tills never defer (L3-8342).
  if (isFrenchCompliant.value) {
    LogService.info(
      `Complete with balance refused on a French till for order ${selectedOrder.value?.reference}`,
      {
        requestId: ctx.requestId,
        orderId: selectedOrder.value?.id,
        orderSessionId: orderSession.id,
      },
      LogLevel.Warning
    );
    NotificationService.showCustomMessage(
      t("partially-paid-booking.not-allowed"),
      "warning"
    );
    return;
  }

  // ORD-733: completing with balance finalizes the sale, so applied discount
  // codes must still be usable — checked before the deferral touches the
  // session, as the backend refuses a dead code at completion anyway.
  if (!(await ensureDiscountCodesUsable(ctx))) {
    return;
  }

  const summary = orderStore.selectedOrderSummary;
  const sessionBalance = activeSessionBalanceOf(summary);

  LogService.info(
    `Completing order ${selectedOrder.value?.reference} with balance`,
    {
      requestId: ctx.requestId,
      orderId: selectedOrder.value?.id,
      orderSessionId: orderSession.id,
      amountDue: summary.amountDue,
      openBalance: summary.openBalance,
      sessionBalance,
    }
  );

  if (sessionBalance > 0) {
    try {
      await orderStore.deferOpenBalance({ sessionId: orderSession.id }, ctx);
    } catch (error) {
      logOrderActionError(
        `deferring the open balance on order ${selectedOrder.value?.reference}`,
        {
          requestId: ctx.requestId,
          orderId: selectedOrder.value?.id,
          orderSessionId: orderSession.id,
        },
        error
      );
      notifyOrderActionError(error);
      throw error;
    }
  }

  // The deferral may already be on the session, so a completion failure must
  // reach the cashier: the button stays offered (amount due > 0) and a retry
  // skips the deferral and only completes. An engine completion failure is
  // journaled by completeOrder.
  try {
    await finalizeOrder(ctx);
  } catch (error) {
    notifyOrderActionError(error);
    throw error;
  }
}

/**
 * Gathers all necessary details for processing a refund.
 * @returns An object containing refund-related details.
 */
function getRefundDetails() {
  const orderStore = useOrderStore();
  const { selectedOrder, selectedOrderSummary: orderSummary } =
    storeToRefs(orderStore);

  const orderMetaStore = useOrderMetaStore();
  const { refundMethod, currency } = storeToRefs(orderMetaStore);

  if (!selectedOrder.value) {
    throw new Error("No selected order available");
  }

  // FIS-719: a cancellation credit is netted against open debt from previous
  // visits, so only the part of the credit that exceeds it is paid back.
  const totalBalance = orderSummary.value.amountDue;
  const paymentCurrency =
    currency.value?.label || selectedOrder.value?.orderSession?.currency;

  const paymentDetails = {
    orderId: selectedOrder.value.id,
    orderSessionId: selectedOrder.value?.orderSession?.id as string,
    // Integer cents: the balance is negative when money is owed back, and
    // Math.round strips float noise (e.g. 19.99 * 100 = 1998.999...)
    value: Math.round(Math.abs(totalBalance) * 100),
    currency: paymentCurrency,
    paymentMethod: refundMethod.value,
  };

  // The order's full money history — refunds are negative payments
  const allPayments = [
    ...orderSummary.value.payments,
    ...orderSummary.value.refunds,
  ];

  return {
    orderSummary,
    totalBalance,
    paymentCurrency,
    paymentDetails,
    allPayments,
  };
}

/**
 * Refund method options for the selected order: only methods the order was
 * actually paid with, each capped at its refundable remainder (maxValue, in
 * cents). Advisory — the server re-validates on refund and also counts
 * refunds still in flight.
 */
export function getRefundMethodOptions(): RefundMethodOption[] {
  const orderStore = useOrderStore();
  const { selectedOrder, selectedOrderSummary } = storeToRefs(orderStore);

  if (!selectedOrder.value) {
    return [];
  }

  const paymentCurrency = (selectedOrder.value.orderSession?.currency ||
    selectedOrderSummary.value.currency) as Currency;

  try {
    return getPaymentService().getAvailableRefundMethods(
      [
        ...selectedOrderSummary.value.payments,
        ...selectedOrderSummary.value.refunds,
      ],
      paymentCurrency
    );
  } catch {
    // Payment service not initialized yet (POS config still loading)
    return [];
  }
}

/**
 * Picks the payment a refund is applied against: candidates are netted by
 * their prior refunds, then exact remainder match → smallest covering →
 * largest (the server guards over-refund).
 */
function pickOriginalPayment(
  refundablePayments: Payment[],
  allPayments: Payment[],
  value: number
): Payment | undefined {
  const refundedByPaymentId = new Map<string, number>();
  for (const payment of allPayments) {
    if (payment.value < 0 && payment.originalPaymentId) {
      refundedByPaymentId.set(
        payment.originalPaymentId,
        (refundedByPaymentId.get(payment.originalPaymentId) || 0) +
          Math.abs(payment.value)
      );
    }
  }

  const candidates = refundablePayments
    .map((payment) => ({
      payment,
      remaining: payment.value - (refundedByPaymentId.get(payment.id) || 0),
    }))
    .filter((candidate) => candidate.remaining > 0);

  const exactMatch = candidates.find((c) => c.remaining === value);
  if (exactMatch) {
    return exactMatch.payment;
  }

  const covering = candidates
    .filter((c) => c.remaining > value)
    .sort((a, b) => a.remaining - b.remaining)[0];
  if (covering) {
    return covering.payment;
  }

  return candidates.sort((a, b) => b.remaining - a.remaining)[0]?.payment;
}

/**
 * Per-refund idempotency key (also the Adyen merchant reference and webhook
 * match key). Fresh per attempt — never reuse one for a different amount.
 */
function generateRefundReference(): string {
  return `refund-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
}

/**
 * Checks if the refund amount can be processed by a payment terminal.
 * @returns `true` if the refund can be processed by terminal, `false` otherwise.
 */
export function canRefundByTerminal(): boolean {
  const { orderSummary, totalBalance, paymentCurrency } = getRefundDetails();

  const paymentService = getPaymentService();
  return paymentService.isAmountRefundableByTerminal({
    payments: [...orderSummary.value.payments, ...orderSummary.value.refunds],
    value: Math.abs(totalBalance) * 100,
    currency: paymentCurrency,
  });
}

/**
 * Initiates a refund process via an Adyen payment terminal.
 * @returns A promise that resolves when the refund process is initiated.
 */
export async function proceedAdyenRefund(): Promise<void> {
  const { paymentDetails, allPayments } = getRefundDetails();
  const ctx = newOperationContext();

  try {
    LogService.info(`Initiating terminal refund`, {
      requestId: ctx.requestId,
      paymentDetails,
    });

    const paymentService = getPaymentService();
    isPaymentInProgress.value = true;
    const response = await paymentService
      .applyRefund({
        // No originalPaymentId: the terminal handles the card reversal itself
        refundMethod: PaymentMethod.POS_ADYEN,
        value: paymentDetails.value,
        orderId: paymentDetails.orderId,
        orderSessionId: paymentDetails.orderSessionId,
        refundReference: generateRefundReference(),
        allPayments,
        currency: paymentDetails.currency as Currency,
      })
      .finally(() => {
        isPaymentInProgress.value = false;
      });

    LogService.info(`Terminal refund result`, {
      orderId: paymentDetails.orderId,
      status: response?.status,
      reason: response?.reason,
      paymentId: response?.payment?.id,
    });

    if (!response || !response.payment) {
      throw new Error("Refund result is null");
    }

    const orderStore = useOrderStore();
    const orderReference = orderStore.selectedOrder?.reference;
    await orderStore.setPayment(response.payment, ctx);
    LogService.info(`Refund payment attached to order ${orderReference}`, {
      requestId: ctx.requestId,
      paymentId: response.payment.id,
      state: response.payment.state,
    });

    // Persist the order with the refund attached so it is durably queued for sync.
    if (orderStore.selectedOrder) {
      await saveOrderForSync(orderStore.selectedOrder);
    }

    if (
      response.status === PaymentStatus.CANCELED ||
      response.status === PaymentStatus.FAILED
    ) {
      // notify user
      const { title, message } = getPaymentStatusNotification(response.reason);
      NotificationService.showCustomMessageWithTitle(message, title, "warning");
    }
  } catch (error) {
    // Swallowed on purpose (the cashier retries from the refund step), so
    // console.error keeps dev visibility while LogService ships it.
    console.error("Refund processing failed:", error);
    logOrderActionError(
      "processing refund",
      { requestId: ctx.requestId, paymentDetails },
      error
    );
    NotificationService.showCustomError(
      error instanceof Error ? error.message : String(error)
    );
  }
}

/**
 * Initiates a regular refund process (e.g., cash).
 * @returns A promise that resolves when the refund process is initiated.
 */
export async function proceedRegularRefund(): Promise<void> {
  const { paymentDetails } = getRefundDetails();
  const ctx = newOperationContext();

  try {
    LogService.info(`Initiating refund`, {
      requestId: ctx.requestId,
      paymentDetails,
    });

    const paymentService = getPaymentService();
    const response = await paymentService.refundValue({
      paymentDetails,
    });

    LogService.info(`Refund result`, {
      orderId: paymentDetails.orderId,
      status: response?.status,
      reason: response?.reason,
      paymentId: response?.payment?.id,
    });

    if (!response || !response.payment) {
      throw new Error("Refund result is null");
    }

    const orderStore = useOrderStore();
    const orderReference = orderStore.selectedOrder?.reference;
    await orderStore.setPayment(response.payment, ctx);
    LogService.info(`Refund payment attached to order ${orderReference}`, {
      requestId: ctx.requestId,
      paymentId: response.payment.id,
      state: response.payment.state,
    });

    // Persist the order with the refund attached so it is durably queued for sync.
    if (orderStore.selectedOrder) {
      await saveOrderForSync(orderStore.selectedOrder);
    }

    if (
      response.status === PaymentStatus.CANCELED ||
      response.status === PaymentStatus.FAILED
    ) {
      // notify user
      const { title, message } = getPaymentStatusNotification(response.reason);
      NotificationService.showCustomMessageWithTitle(message, title, "warning");
    }
  } catch (error) {
    // Swallowed on purpose (the cashier retries from the refund step), so
    // console.error keeps dev visibility while LogService ships it.
    console.error("Refund processing failed:", error);
    logOrderActionError(
      "processing refund",
      { requestId: ctx.requestId, paymentDetails },
      error
    );
  }
}

/**
 * Online card refund through the payment service. The refund is recorded
 * server-side and comes back PENDING with a server-owned obligation the
 * Adyen webhook settles later — mirrored locally so the balance nets to 0
 * and the order completes normally.
 */
export async function proceedOnlineRefund(): Promise<void> {
  const { paymentDetails, allPayments } = getRefundDetails();
  const refundMethod = paymentDetails.paymentMethod;

  const refundOption = getRefundMethodOptions().find(
    (option) => option.method === refundMethod
  );
  const originalPayment = refundOption
    ? pickOriginalPayment(
        refundOption.refundablePayments,
        allPayments,
        paymentDetails.value
      )
    : undefined;

  if (!originalPayment) {
    LogService.info(
      `No refundable ${refundMethod} payment found on order ${paymentDetails.orderId}`
    );
    NotificationService.showGeneralResponseError();
    return;
  }

  const orderStore = useOrderStore();
  isPaymentInProgress.value = true;
  try {
    const paymentService = getPaymentService();

    // The server records the refund into the active PENDING session — persist
    // it (with the cancellations) before the refund call
    await orderStore.persistOrder();

    // Keep the IndexedDB copy aligned with the persisted session
    if (orderStore.selectedOrder) {
      await saveOrderForSync(orderStore.selectedOrder);
    }

    const response = await paymentService.applyRefund({
      refundMethod,
      value: paymentDetails.value,
      orderId: paymentDetails.orderId,
      orderSessionId: paymentDetails.orderSessionId,
      originalPaymentId: originalPayment.id,
      refundReference: generateRefundReference(),
      allPayments,
      currency: paymentDetails.currency as Currency,
    });

    // No payment check here — a PENDING refund has an obligation instead
    if (!response) {
      throw new Error("Refund result is null");
    }

    if (
      response.status === PaymentStatus.CANCELED ||
      response.status === PaymentStatus.FAILED
    ) {
      // notify user (e.g. refund refused or no internet connection)
      const { title, message } = getPaymentStatusNotification(response.reason);
      NotificationService.showCustomMessageWithTitle(message, title, "warning");
      return;
    }

    if (response.status === PaymentStatus.PENDING) {
      // Online refund: the server recorded a ToPayType.REFUND obligation on
      // the active session; the money moves when the Adyen webhook settles it.
      LogService.info(
        `Online refund pending settlement for order: ${paymentDetails.orderId} refundReference: ${response.refundReference}`,
        { params: { obligation: response.obligation } }
      );

      if (response.obligation) {
        // Mirror the server-owned obligation locally: the balance nets to 0
        // so the cashier can complete the order right away.
        orderStore.setRefundObligation(response.obligation);
      } else {
        // Fallback: pull the obligation's ToPay row from the server
        const orderReference = orderStore.selectedOrder?.reference;
        if (orderReference) {
          await fetchOrderByReference(orderReference);
        }
      }

      NotificationService.showCustomMessageWithTitle(
        String(t("adyen-refund.in-progress-message")),
        String(t("adyen-refund.in-progress")),
        "warning"
      );
      return;
    }

    // A settled refund must carry the recorded payment
    if (!response.payment) {
      throw new Error("Refund result is null");
    }

    // Manual refund settled: the server already recorded it into the active
    // session, so refresh the order instead of re-recording locally
    // (setPayment would regenerate the payment id and decrement toPay).
    const orderReference = orderStore.selectedOrder?.reference;
    if (orderReference) {
      await fetchOrderByReference(orderReference);
    }
  } catch (error) {
    console.error("Refund processing failed:", error);
    LogService.info(
      `Refund failed for order: ${paymentDetails.orderId} method: ${refundMethod}`,
      { error }
    );
    NotificationService.showCustomError(
      error instanceof Error ? error.message : String(error)
    );
    // The server may have recorded the refund even though the client-side
    // tail failed (e.g. response lost) — refresh so its state shows.
    const orderReference = orderStore.selectedOrder?.reference;
    if (orderReference) {
      await fetchOrderByReference(orderReference).catch(() => undefined);
    }
  } finally {
    isPaymentInProgress.value = false;
  }
}

/**
 * Returns the translated title and message for a given payment status.
 * This is used to show consistent notifications for payment failures.
 * @param reason - The status reason from the payment service.
 * @returns An object containing the title and message for the notification.
 */
function getPaymentStatusNotification(
  reason: TerminalPaymentServiceStatus | undefined
): { title: string; message: string } {
  let message = "";
  let title = "";

  switch (reason) {
    case TerminalPaymentServiceStatus.Aborted:
      title = String(t("payment-service.aborted"));
      message = String(t("payment-service.aborted-description"));
      break;
    case TerminalPaymentServiceStatus.Canceled:
      title = String(t("payment-service.canceled"));
      message = String(t("payment-service.canceled-description"));
      break;
    case TerminalPaymentServiceStatus.Refusal:
      title = String(t("payment-service.refusal"));
      message = String(t("payment-service.refusal-description"));
      break;
    case TerminalPaymentServiceStatus.InvalidCard:
      title = String(t("payment-service.invalid-card"));
      message = String(t("payment-service.invalid-card-description"));
      break;
    case TerminalPaymentServiceStatus.WrongPIN:
      title = String(t("payment-service.wrong-pin"));
      message = String(t("payment-service.wrong-pin-description"));
      break;
    case TerminalPaymentServiceStatus.DeviceOut:
      title = String(t("payment-service.device-out"));
      message = String(t("payment-service.device-out-description"));
      break;
    case TerminalPaymentServiceStatus.LowBattery:
      title = String(t("payment-service.low-battery"));
      message = String(t("payment-service.low-battery-description"));
      break;
    case TerminalPaymentServiceStatus.Busy:
      title = String(t("payment-service.busy"));
      message = String(t("payment-service.busy-description"));
      break;
    case TerminalPaymentServiceStatus.UnreachableHost:
      title = String(t("payment-service.unreachable-host"));
      message = String(t("payment-service.unreachable-host-description"));
      break;
    case TerminalPaymentServiceStatus.NotAllowed:
      title = String(t("payment-service.not-allowed"));
      message = String(t("payment-service.not-allowed-description"));
      break;
    // TODO: add translation keys
    case TerminalPaymentServiceStatus.CantCheckStatus:
      title = String(t("payment-service.cant-check-status"));
      message = String(t("payment-service.cant-check-status-description"));
      break;
    case TerminalPaymentServiceStatus.NotFound:
      title = String(t("payment-service.not-found"));
      message = String(t("payment-service.not-found-description"));
      break;
    case TerminalPaymentServiceStatus.UnknownError:
      title = String(t("payment-service.unknown"));
      message = String(t("payment-service.unknown-description"));
      break;
    case TerminalPaymentServiceStatus.GroupInfoMissing:
      title = String(t("payment-service.missing-group-info"));
      message = String(t("payment-service.missing-group-info-description"));
      break;
    case TerminalPaymentServiceStatus.UnableToFetchGroupInfo:
      title = String(t("payment-service.unable-to-fetch-group-info"));
      message = String(
        t("payment-service.unable-to-fetch-group-info-description")
      );
      break;
    default:
      message = String(t("payment-service.unknown"));
      break;
  }
  return { title, message };
}
